import {
  REFUSAL_MESSAGES,
  PASS_MESSAGES,
  RestrictedSourceTypes,
  FileTypes,
  META_FILE_REGEX,
  isDisplayableMediaType,
} from '@/shared/constants';
import {
  MediaSource,
  Source,
  SourceContext,
  TimezoneDetails,
  SOURCE_TYPES,
  MediaType,
  Slide,
} from './types';
import { Time, CalendarDate } from "@internationalized/date";
import { PressEvent } from "@heroui/react";
import SparkMD5 from 'spark-md5';
import type { EventFromChat } from '@/components/answer-item';
import deepEqual from 'deep-equal';
import extractDate from 'extract-date';
import extractTime from 'extract-time';
import { jwtDecode } from 'jwt-decode';
import { v5 as uuidv5 } from 'uuid';
import { tpFetch } from '@/lib/http/client';
import { resetAppNavSession } from '@/lib/app-nav-state';
import { trackEvent as trackIntercomEvent } from '@intercom/messenger-js-sdk';
import {
  fetchProvenanceFileInfo,
  fetchProvenanceFileInfoBatch,
  ProvenanceApiError,
  type FileSummary,
  type ProvenanceFileInfoBody,
  type ProvenanceFileInfoFile,
  type ProvenanceSubdocumentInfo,
} from '@/shared/cors-provenance-api';
import { getGatewaySession } from '@/lib/gateway-fetch';
import { AD_HOC_CASE_DISPLAY_NAME, isPersonalFilesCaseId } from '@/shared/ad-hoc-case';
import { postTelemetryEvent, fetchDownloadUrl } from '@/shared/cors-edge-lambda-api';

/** Media types the UI can preview/play — keep in sync with ``constants.UsableMediaTypes``.
 * ``json`` is included here so citation path resolution prefers a dispatched
 * ``.json`` extract, but display is still gated by ``isDisplayableMediaType`` /
 * ``jsonMediaDisplay`` or ``mediaPlayerTransition.json`` at every UI call site. */
const USABLE_MEDIA_TYPES: readonly MediaType[] = [
  'image',
  'video',
  'audio',
  'pdf',
  'text',
  'markdown',
  'docx',
  'pptx',
  'html',
  'spreadsheet',
  'json',
];

/** Re-export provenance file-info types for consumers that import from ``@/shared/functions``. */
export type {
  ProvenanceFileInfoBody as ProvenanceFileInfoResponse,
  ProvenanceFileInfoFile as ProvenanceFileInfo,
  ProvenanceSubdocumentInfo,
  FileSummary,
};

interface TimeParts {
  hoursNumber: number | undefined;
  minutesNumber: number | undefined;
  secondsNumber: number | undefined;
}

const US_TIMEZONES_PLUS_UTC = [
  { name: "America/New_York", abbreviation: "Eastern Time", offset: "GMT-5" , offsetInMilliseconds: -18000000 },
  { name: "America/Chicago", abbreviation: "Central Time", offset: "GMT-6" , offsetInMilliseconds: -21600000 },
  { name: "America/Denver", abbreviation: "Mountain Time", offset: "GMT-7" , offsetInMilliseconds: -25200000 },
  { name: "America/Los_Angeles", abbreviation: "Pacific Time", offset: "GMT-8" , offsetInMilliseconds: -28800000 },
  { name: "America/Anchorage", abbreviation: "Alaska Time", offset: "GMT-9" , offsetInMilliseconds: -32400000 },
  { name: "Pacific/Honolulu", abbreviation: "Hawaii-Aleutian Time", offset: "GMT-10" , offsetInMilliseconds: -36000000 },
  { name: "UTC", abbreviation: "UTC", offset: "GMT" , offsetInMilliseconds: 0 },
];

// This is somewhat brittle as it will always match against any file saved as records.html or preservation-[number].html
// but that's a risk we're already taking and are okay with it for now. Meta here refers to a file from Meta (aka Facebook) not
// and actual metadata file.
export const isMetaBundleFilename = (url: string | undefined): boolean => {
  if (!url) return false;
  const base = storagePathForExtensionLookup(url).split('/').pop() || '';
  return META_FILE_REGEX.test(base);
};

export const isMetaFile = (mediaItem?: MediaSource) => {
  if (!mediaItem) return false;
  // Include `markdown`: TAI preview .md threads are typed as markdown when preview content-type is mapped (#775).
  const isTextHtmlOrMarkdown =
    mediaItem.mediaType === 'text' ||
    mediaItem.mediaType === 'html' ||
    mediaItem.mediaType === 'markdown';
  if (!isTextHtmlOrMarkdown) return false;
  // Preview/dispatched paths (e.g. markdown/threads/*.md) replace mediaUrl; keep detecting the underlying Meta bundle.
  const candidateUrls = [
    mediaItem.sourceUrl,
    mediaItem.mediaUrl,
    mediaItem.originalUrl,
    mediaItem.sourceFilePath,
    mediaItem.mediaSrc,
    mediaItem.previewFilePath,
  ];
  return candidateUrls.some((url) => isMetaBundleFilename(url));
};

/**
 * Returns a stable key for the underlying file URL loaded (preview vs source, XML-derived PDF, etc.).
 * Used by PdfViewer and MediaDisplay to reset state when the displayed asset changes in previews.
 * Must align with the URL actually fetched — not `originalUrl` alone as that may change due to preview generation.
 *
 * This needs to be in shared functions because it is used in both the PdfViewer and MediaDisplay components and
 * we have to avoid circular dependencies.
 */
export const getMediaSourceUrlKey = (media: MediaSource | undefined): string => {
  if (!media) return '';
  // Use || so '' does not block the next field (mediaDisplay may clear mediaSrc while mediaUrl is set).
  const explicit = media.srcToUse || media.mediaSrc || media.mediaUrl;
  const previewSide =
    media.fileInfo?.previewUrl ||
    media.previewFilePath ||
    media.derived_object_url ||
    '';

  if (explicit) {
    // Same mediaUrl can denote different assets (e.g. native PDF vs XML-derived preview). Do not return early on URL alone.
    if (previewSide && previewSide !== explicit) {
      return `${explicit}|${previewSide}`;
    }
    return explicit;
  }
  if (previewSide) return previewSide;
  if (media.sourceFilePath) return media.sourceFilePath;
  return media.originalUrl || media.mediaContext?.previewUrl || '';
};

export type PreferredSourceDisplay = {
  mediaUrl: string | undefined;
  mediaType: MediaType;
  selectedBy:
    | 'previewFilePath'
    | 'dispatchedFilePath'
    | 'derivedPreview'
    | 'fileInfoFileUrl'
    | 'sourceFilePath'
    | 'none';
};

export const getDispatchedFilePath = (
  source:
    | (Partial<Source> & { dispatched_file_path?: string })
    | (Partial<MediaSource> & { dispatched_file_path?: string })
    | null
    | undefined,
): string => {
  if (!source) return '';
  const raw =
    source.dispatchedFilePath ||
    source.dispatched_file_path ||
    source.sourceContext?.dispatchedFilePath ||
    source.sourceContext?.dispatched_file_path;
  return typeof raw === 'string' ? raw.trim() : '';
};

/** True when provenance ``fileInfo`` describes ``path`` (not a sibling citation url). */
function fileInfoMatchesPath(
  fileInfo: FileSummary | null | undefined,
  path: string,
): boolean {
  if (!fileInfo || !path.trim()) return false;
  const normalizedPath = path.trim().toLowerCase();
  const fileInfoPaths = [
    fileInfo.fileUrl,
    fileInfo.searchablePath,
    fileInfo.previewUrl,
    fileInfo.fileName,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  return fileInfoPaths.some((candidate) => {
    const normalizedCandidate = candidate.trim().toLowerCase();
    return (
      normalizedCandidate === normalizedPath ||
      normalizedPath.endsWith(normalizedCandidate) ||
      normalizedCandidate.endsWith(normalizedPath)
    );
  });
}

/** Minimal source shape for typing a single path — no sibling citation fields. */
function sourceLikeForPathOnly(source: Source | MediaSource, path: string): Source {
  return {
    sourceFilePath: path,
    sourceUrl: path,
    mediaUrl: path,
    mediaSrc: path,
    previewFilePath: '',
    dispatchedFilePath: '',
    sourceType: source.sourceType,
    mediaType: source.mediaType,
  } as Source;
}

function mediaTypeForPathWithFileInfo(
  source: Source | MediaSource,
  fileInfo: FileSummary | null | undefined,
  path: string,
): MediaType {
  const scopedFileInfo = fileInfoMatchesPath(fileInfo, path) ? fileInfo : undefined;
  return getMediaType(sourceLikeForPathOnly(source, path), scopedFileInfo);
}

function mediaTypeForPathWithoutFileInfo(
  source: Source | MediaSource,
  path: string,
): MediaType {
  return getMediaType(sourceLikeForPathOnly(source, path), undefined);
}

/** True when a content-category token marks container/archive lineage. */
const isContainerLineageCategory = (category: string): boolean => {
  const c = category.toLowerCase();
  return c.includes('container') || c === 'archive' || c === 'cellebrite';
};

/**
 * Drop inherited container/archive lineage tokens from a subdocument's
 * content categories, leaving only the self-describing categories (``image``,
 * ``video``, …). Genuine nested containers are still detected by their own
 * extension / mime in ``isContainerLikeSource``.
 */
export const stripContainerLineageCategories = (
  categories: string[] | null | undefined,
): string[] =>
  (Array.isArray(categories) ? categories : []).filter(
    (c) => !isContainerLineageCategory(String(c)),
  );

/** Container/archive extensions recognized by their own file type (not lineage). */
const CONTAINER_SELF_EXTENSIONS = [
  'zip',
  'pst',
  'ost',
  'ufdr',
  'tar',
  'gz',
  'tgz',
  'bz2',
  '7z',
  'rar',
  'xz',
];

/**
 * True when ``path`` is a *virtual* searchable archive-member path
 * (``data/bulk/…/archive.zip/inner.png``, ``…/mail.mbox/email_1/…``) that is
 * not a real S3 object — ``/api/proxy`` and ``/files/file-info`` 404 on them.
 *
 * Processed extracts often keep an ``archive.zip/member`` suffix under
 * ``files/<hash>/…``; those are real objects and must NOT match.
 */
export function isArchiveMemberSearchablePath(path: string | undefined | null): boolean {
  const p = (path ?? '').trim();
  if (!p) return false;
  const containerAlt = [...CONTAINER_SELF_EXTENSIONS, 'mbox', 'mbx'].join('|');
  if (!new RegExp(`\\.(?:${containerAlt})/`, 'i').test(p)) return false;

  // Presigned / absolute HTTP URLs are always playable.
  if (/^https?:\/\//i.test(p)) return false;

  const objectKey = (() => {
    if (/^s3:\/\//i.test(p)) {
      const withoutScheme = p.slice('s3://'.length);
      const slash = withoutScheme.indexOf('/');
      return slash >= 0 ? withoutScheme.slice(slash + 1) : '';
    }
    return p.replace(/^\/+/, '');
  })();

  // Provenance processed outputs live under ``files/<md5>/…`` even when the
  // key retains the archive-relative path (``…/zip_test_data.zip/inner.png``).
  if (/^files\//i.test(objectKey)) return false;

  return true;
}

/**
 * Parent archive/mbox path for a virtual member
 * (``data/bulk/…/archive.zip/inner.png`` → ``data/bulk/…/archive.zip``).
 * Null when ``path`` is not an archive-member searchable path.
 */
export function archiveContainerSourceKey(path: string | null | undefined): string | null {
  const p = (path ?? '').trim();
  if (!isArchiveMemberSearchablePath(p)) return null;
  const containerAlt = [...CONTAINER_SELF_EXTENSIONS, 'mbox', 'mbx'].join('|');
  const match = p.match(new RegExp(`^(.*\\.(?:${containerAlt}))/`, 'i'));
  return match?.[1] ?? null;
}

/** Archive mime types that mark a file as a container regardless of extension. */
const CONTAINER_SELF_MIME_TYPES = new Set([
  'application/zip',
  'application/x-zip-compressed',
  'application/x-tar',
  'application/gzip',
  'application/x-gzip',
  'application/x-7z-compressed',
  'application/x-rar-compressed',
  'application/vnd.rar',
  'application/x-bzip2',
  'application/x-xz',
  'application/vnd.ms-outlook',
]);

export function isContainerLikeSource(
  source: Source | MediaSource,
  fileInfo: FileSummary | null | undefined,
  options?: { includeMbox?: boolean },
): boolean {
  const includeMbox = options?.includeMbox ?? true;
  const containerExtensions = new Set([
    ...CONTAINER_SELF_EXTENSIONS,
    ...(includeMbox ? ['mbox', 'mbx'] : []),
  ]);
  // Content-category heuristic. Subdocument categories are projected through
  // ``fileInfoFromProvenanceBody``, which strips the enclosing container's
  // lineage tag, so an image extracted from a zip is judged by its own
  // mime / extension below rather than the archive it came from.
  const categories = fileInfo?.contentCategories || source.fileInfo?.contentCategories || [];
  if (categories.some((c) => isContainerLineageCategory(String(c)))) {
    return true;
  }
  const mimeType = (fileInfo?.mimeType || source.fileInfo?.mimeType || '').toLowerCase();
  if (mimeType && CONTAINER_SELF_MIME_TYPES.has(mimeType)) return true;
  const sourceFileType = (
    fileInfo?.extension ||
    source.fileInfo?.extension ||
    ''
  ).toLowerCase();
  if (sourceFileType === 'container' || containerExtensions.has(sourceFileType)) return true;
  const candidatePath =
    source.sourceFilePath ||
    source.sourceUrl ||
    (source as MediaSource).mediaUrl ||
    (source as MediaSource).mediaSrc ||
    source.previewFilePath ||
    source.derived_object_url ||
    fileInfo?.fileUrl ||
    fileInfo?.previewUrl ||
    '';
  const extension = extensionFromStoragePath(candidatePath);
  return containerExtensions.has(extension);
}

function basenameFromStoragePath(path: string): string {
  const cleaned = path.split('?')[0].split('#')[0];
  const segments = cleaned.split('/').filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : '';
}

/** Read ``searchablePath`` from camelCase or legacy snake_case citation payloads. */
export function getCitationSearchablePath(source: Source): string {
  const extended = source as Source & { searchable_path?: string };
  return (source.searchablePath ?? extended.searchable_path ?? '').trim();
}

/**
 * Path inside a container archive/mbox after the ``.zip/`` / ``.mbox/`` marker,
 * or ``undefined`` when the citation is not an archive member.
 */
export function resolveArchiveMemberInnerRelativePath(
  source: Source,
): string | undefined {
  const searchable = getCitationSearchablePath(source);
  if (!searchable) return undefined;
  const containerAlt = [...CONTAINER_SELF_EXTENSIONS, 'mbox', 'mbx'].join('|');
  const match = searchable.match(new RegExp(`\\.(${containerAlt})/(.+)$`, 'i'));
  const inner = match?.[2]?.trim();
  return inner || undefined;
}

function segmentHasKnownMediaExtension(segment: string): boolean {
  const ext = extensionFromStoragePath(segment);
  return !!ext && Object.prototype.hasOwnProperty.call(FileTypes, ext);
}

/**
 * Display name for the responsive file cited inside a container (or the source
 * itself for top-level files). Prefers dispatched basenames, then the deepest
 * archive-relative segment with a known media extension, then the full inner
 * relative path for folder-level citations. Skips ``previewFilePath`` — it is
 * only a render proxy (e.g. a .webp for a .heic) and must not name the Item.
 */
export function resolveCitationInnerFileName(source: Source): string {
  const dispatched = getDispatchedFilePath(source);
  if (dispatched) {
    const inner = basenameFromStoragePath(dispatched);
    if (inner) return inner;
  }

  const innerRelative = resolveArchiveMemberInnerRelativePath(source);
  if (innerRelative) {
    const segments = innerRelative.split('/').filter(Boolean);
    for (let i = segments.length - 1; i >= 0; i--) {
      const segment = segments[i]!;
      if (segmentHasKnownMediaExtension(segment)) {
        return segment;
      }
    }
    return innerRelative;
  }

  const searchable = getCitationSearchablePath(source);
  if (searchable) {
    const inner = basenameFromStoragePath(searchable);
    if (inner) return inner;
  }

  const sourcePath = source.sourceFilePath || source.sourceUrl || '';
  return basenameFromStoragePath(sourcePath) || 'Source';
}

/**
 * Secondary location line for citation popovers: page number, inner archive
 * relative path, or truncated top-level path — never the container zip alone.
 */
export function resolveCitationDisplayLocation(
  source: Source,
  fileInfo?: FileSummary | null,
): string {
  const page = resolveCitationDisplayPage(source);
  if (page != null) return `Page ${page}`;

  const innerRelative = resolveArchiveMemberInnerRelativePath(source);
  if (
    innerRelative &&
    isContainerLikeSource(source, fileInfo ?? source.fileInfo ?? null)
  ) {
    return innerRelative;
  }

  return source.truncatedFilePath ?? '';
}

/**
 * Container basename when a citation references media inside an archive. Returns
 * ``undefined`` for ordinary top-level files or when inner and container names
 * are the same.
 */
export function resolveCitationContainerFileName(
  source: Source,
  fileInfo?: FileSummary | null,
): string | undefined {
  const innerName = resolveCitationInnerFileName(source);
  const containerPath = source.sourceFilePath || source.sourceUrl || '';
  const containerName = basenameFromStoragePath(containerPath);
  if (!containerName) return undefined;
  if (!isContainerLikeSource(source, fileInfo ?? source.fileInfo ?? null)) return undefined;
  if (containerName.toLowerCase() === innerName.toLowerCase()) return undefined;
  return containerName;
}

/**
 * True when create-event would attach the container itself (zip, pst, mbox, …),
 * not a cited extract inside it.
 *
 * Display URL is the wrong signal: a container can resolve to a non-container
 * ``derivedPreview`` / thumbnail while ``handleCreateEventFromSource`` still
 * builds the event from the archive ``sourceFilePath``. A dispatched extract
 * or a non-AV cited ``previewFilePath`` excerpt is a real subdocument and
 * stays eligible.
 */
export function isContainerSelfSource(
  source: Source | MediaSource | undefined,
  fileInfo?: FileSummary | null,
): boolean {
  if (!source) return false;

  const dispatched = getDispatchedFilePath(source);
  if (dispatched && !isContainerLikeSource({ sourceFilePath: dispatched } as Source, null)) {
    return false;
  }

  const previewFilePath =
    typeof source.previewFilePath === 'string' ? source.previewFilePath.trim() : '';
  if (previewFilePath && !isArchiveMemberSearchablePath(previewFilePath)) {
    const previewType = getMediaType(
      sourceLikeForPathOnly(source, previewFilePath),
      undefined,
    );
    const isAvPreview =
      previewType === 'image' || previewType === 'video' || previewType === 'audio';
    // Same gate as ``resolvePreferredMediaDisplayForSource``: a non-AV cited
    // excerpt is the selected subdocument, not a thumbnail of the archive.
    if (
      !isAvPreview &&
      previewType !== 'json' &&
      USABLE_MEDIA_TYPES.includes(previewType) &&
      !isContainerLikeSource({ sourceFilePath: previewFilePath } as Source, null)
    ) {
      return false;
    }
  }

  const identityPath =
    (typeof source.sourceFilePath === 'string' && source.sourceFilePath.trim()) ||
    (typeof source.sourceUrl === 'string' && source.sourceUrl.trim()) ||
    '';
  if (isArchiveMemberSearchablePath(identityPath)) {
    return false;
  }

  return isContainerLikeSource(source, fileInfo ?? source.fileInfo);
}

/**
 * True for a top-level Cellebrite ``.ufdr`` archive (the container itself),
 * not an extract living under ``…/report.ufdr/inner/file``.
 *
 * Evidence locker rows use this to show a magnify+ preview that opens
 * ``MobileForensicViewer`` instead of hiding the control as a generic container.
 */
export function isUfdrRootSource(
  source: Source | MediaSource | { sourceFilePath?: string | null } | undefined,
  fileInfo?: FileSummary | null,
): boolean {
  if (!source) return false;
  const asSource = source as Source;
  if (!isContainerSelfSource(asSource, fileInfo)) return false;

  const extFromInfo = (
    fileInfo?.extension ||
    asSource.fileInfo?.extension ||
    ''
  ).toLowerCase();
  if (extFromInfo === 'ufdr') return true;

  const identityPath =
    getDispatchedFilePath(asSource) ||
    (typeof asSource.sourceFilePath === 'string' ? asSource.sourceFilePath.trim() : '') ||
    (typeof asSource.sourceUrl === 'string' ? asSource.sourceUrl.trim() : '') ||
    '';
  return extensionFromStoragePath(identityPath) === 'ufdr';
}

/**
 * True for a top-level ``.zip`` container. The preview opens the same viewer
 * as a ``.ufdr``. Marker detection happens after the tree loads.
 */
export function isZipExportRoot(
  source: Source | MediaSource | { sourceFilePath?: string | null } | undefined,
  fileInfo?: FileSummary | null,
): boolean {
  if (!source) return false;
  const asSource = source as Source;
  if (!isContainerSelfSource(asSource, fileInfo)) return false;

  const extFromInfo = (
    fileInfo?.extension ||
    asSource.fileInfo?.extension ||
    ''
  ).toLowerCase();
  if (extFromInfo === 'zip') return true;

  const identityPath =
    getDispatchedFilePath(asSource) ||
    (typeof asSource.sourceFilePath === 'string' ? asSource.sourceFilePath.trim() : '') ||
    (typeof asSource.sourceUrl === 'string' ? asSource.sourceUrl.trim() : '') ||
    '';
  return extensionFromStoragePath(identityPath) === 'zip';
}

const CELLEBRITE_EXPORT_NAMES = new Set([
  'messages.json',
  'contacts.csv',
  'device_selectors.csv',
  'summary.md',
  'ufdr_calllog.md',
  'call_log.json',
  'calllog.json',
  'call_log.csv',
  'ufdr_calllog.json',
  'messages.md',
  'ufdr_processing_messages.md',
  'report.xml',
]);

/**
 * True when the first level of a zip looks like a Cellebrite export:
 * a ``.ufdr`` or ``.ufd``, ``report.xml``, a ``UFDReader`` folder, or a known sidecar.
 */
export function isCellebriteExportSource(
  entries: Array<{ name?: string | null; path?: string | null; nodeType?: string | null }>,
): boolean {
  return entries.some((entry) => {
    const base = (entry.name || entry.path || '').split('/').filter(Boolean).pop()?.toLowerCase() ?? '';
    if (!base) return false;
    if (base.endsWith('.ufdr') || base.endsWith('.ufd')) return true;
    if (base === 'ufdreader') return true;
    return CELLEBRITE_EXPORT_NAMES.has(base);
  });
}

/**
 * When a zip's first level is a ``.ufdr`` directory and the rest is not already
 * a Cellebrite export, sidecars live inside that member. Returns its path.
 */
export function nestedUfdrMemberPath(
  entries: Array<{ name?: string | null; path?: string | null; nodeType?: string | null }>,
): string | undefined {
  const firstLevel = entries.filter((entry) => !(entry.path || entry.name || '').includes('/'));
  const nested = firstLevel.find(
    (entry) => /\.ufdr$/i.test(entry.name || entry.path || '') && entry.nodeType === 'directory',
  );
  if (!nested || isCellebriteExportSource(firstLevel.filter((entry) => entry !== nested))) return undefined;
  return nested.path || nested.name || undefined;
}

function extensionFromMediaPath(path: string): string {
  return extensionFromStoragePath(path);
}

/** Source extensions that map to ``video`` but are not reliably playable in ``<video>`` — prefer ``previewUrl``. */
const NON_NATIVE_HTML5_VIDEO_EXTENSIONS = new Set([
  'avi', 'mkv', 'flv', 'wmv', 'mpeg', 'mpg', 'dav',
]);

/** Audio extensions that need a server ``previewUrl`` for in-browser playback. */
const NON_NATIVE_HTML5_AUDIO_EXTENSIONS = new Set(['amr', 'wma', 'slk']);

/** Image extensions browsers cannot render reliably without a transcode. */
const LIMITED_BROWSER_IMAGE_EXTENSIONS = new Set(['heic', 'heif', 'tif', 'tiff']);

const PAGED_DOCUMENT_MEDIA_TYPES: MediaType[] = ['pdf', 'docx', 'pptx'];
const TEXT_LIKE_MEDIA_TYPES: MediaType[] = ['markdown', 'text', 'html'];

/** True when a markdown/text/html proxy would replace a PDF/Office document. */
function isTextProxyOfPagedDocument(
  originType: MediaType,
  previewType: MediaType,
): boolean {
  return (
    PAGED_DOCUMENT_MEDIA_TYPES.includes(originType) &&
    TEXT_LIKE_MEDIA_TYPES.includes(previewType)
  );
}

/** A generated markdown file whose preview is a PDF should open in the PDF viewer. */
function mediaTypeForDerivedPreview(originType: MediaType, previewType: MediaType): MediaType {
  if (
    TEXT_LIKE_MEDIA_TYPES.includes(originType) &&
    PAGED_DOCUMENT_MEDIA_TYPES.includes(previewType)
  ) {
    return previewType;
  }
  return originType;
}

/**
 * Keep media display selection consistent between SourceCard, answer-item citations,
 * and timeline events.
 *
 * Priority (first non-empty wins):
 * 1. Chat citation paths — ``previewFilePath`` / ``dispatchedFilePath`` (container
 *    ordering preserved for UFDR text-vs-AV extracts).
 * 2. Provenance transcode — ``fileInfo.previewUrl`` / ``derived_object_url``.
 * 3. Canonical source path or ``fileInfo.fileUrl`` when the above are absent.
 */
export const resolvePreferredMediaDisplayForSource = (
  source: Source | MediaSource | null | undefined,
  fileInfoIn?: FileSummary | null | undefined,
): PreferredSourceDisplay => {
  if (!source) {
    return {
      mediaUrl: undefined,
      mediaType: 'unknown' as const,
      selectedBy: 'none' as const,
    };
  }
  const fileInfo = fileInfoIn || source.fileInfo;
  const previewFilePath = typeof source.previewFilePath === 'string' ? source.previewFilePath.trim() : '';
  const dispatchedFilePath = getDispatchedFilePath(source);
  // Judge container-ness from the object ``fileInfo`` describes (its own
  // ``fileUrl`` / mime / extension), not from a virtual searchable path that
  // may contain a parent ``.zip``/``.mbox`` segment. Otherwise a zip-extract
  // row whose file-info fetch accidentally returned the parent archive is
  // treated as container-self and never gets a playable URL.
  const containerJudgePath =
    (typeof fileInfo?.fileUrl === 'string' && fileInfo.fileUrl.trim()) ||
    (typeof (source as MediaSource).s3Url === 'string' && (source as MediaSource).s3Url!.trim()) ||
    '';
  const isContainer = isContainerLikeSource(
    containerJudgePath ? sourceLikeForPathOnly(source, containerJudgePath) : source,
    fileInfo,
  );
  const sourceFilePath =
    (typeof source.sourceFilePath === 'string' ? source.sourceFilePath.trim() : '') ||
    (typeof source.sourceUrl === 'string' ? source.sourceUrl.trim() : '') ||
    (typeof (source as MediaSource).mediaUrl === 'string' ? (source as MediaSource).mediaUrl?.trim() : '') ||
    (typeof (source as MediaSource).mediaSrc === 'string' ? (source as MediaSource).mediaSrc?.trim() : '') ||
    '';

  // A citation can carry two urls at once — the ``dispatchedFilePath`` (the cited
  // object: an extract at any depth inside a container, or the source itself) and
  // a ``previewFilePath`` proxy. How they rank depends on what the preview proxy
  // actually is (typed from its own extension / file-info):
  //
  //  - A NON-AV, NON-JSON ``previewFilePath`` (a cited ``.txt`` / document excerpt)
  //    is the content the user selected. It wins over the dispatched object so we
  //    never mount an audio/video player — or dump a raw ``.eml`` — for what was a
  //    text citation whose ``dispatchedFilePath`` points at a media/email sibling
  //    (TP-2570).
  //  - ``json`` is in ``USABLE_MEDIA_TYPES`` so a *dispatched* ``.json`` extract
  //    resolves, but a JSON ``previewFilePath`` must NOT short-circuit like a text
  //    excerpt: when JSON media flags are off, UI gates drop JSON and the user
  //    would lose the real dispatched video/audio/etc. preview entirely.
  //  - An IMAGE ``previewFilePath`` (a thumbnail jpg / waveform png of an AV
  //    extract) is only a static stand-in; it loses to the real dispatched media
  //    resolved below (TR-1111). It is picked up by the fallback further down
  //    only when the dispatched object cannot be typed yet.
  const previewType = previewFilePath
    ? getMediaType(
        sourceLikeForPathOnly(source, previewFilePath),
        fileInfoMatchesPath(fileInfo, previewFilePath) ? fileInfo : undefined,
      )
    : ('unknown' as MediaType);
  const isAvMediaType = (mediaType: MediaType) =>
    mediaType === 'image' || mediaType === 'video' || mediaType === 'audio';
  const originPathForPreviewGate = dispatchedFilePath || sourceFilePath;
  const originTypeForPreviewGate = originPathForPreviewGate
    ? getMediaType(
        sourceLikeForPathOnly(source, originPathForPreviewGate),
        fileInfoMatchesPath(fileInfo, originPathForPreviewGate) ? fileInfo : undefined,
      )
    : ('unknown' as MediaType);
  // A PDF/docx/pptx should render as a document (or its captured still), not the
  // markdown/text extract junkdrawer stores as previewFilePath.
  if (
    previewFilePath &&
    !isArchiveMemberSearchablePath(previewFilePath) &&
    previewType !== 'json' &&
    USABLE_MEDIA_TYPES.includes(previewType) &&
    !isAvMediaType(previewType) &&
    !isTextProxyOfPagedDocument(originTypeForPreviewGate, previewType)
  ) {
    return {
      mediaUrl: previewFilePath,
      mediaType: previewType,
      selectedBy: 'previewFilePath' as const,
    };
  }

  // The cited object we display is the dispatched file or, when there is no
  // dispatch, the source file itself. That object is the identity we type from
  // provenance file-info; when provenance produced a web-friendly ``previewUrl``
  // (transcoded mp4, webp for HEIC, markdown for an ``.eml``, …) that render URL
  // is what we actually show.
  const displayTarget = dispatchedFilePath || sourceFilePath;
  if (displayTarget) {
    const scopedFileInfo = fileInfoMatchesPath(fileInfo, displayTarget) ? fileInfo : undefined;
    const mediaType = getMediaType(sourceLikeForPathOnly(source, displayTarget), scopedFileInfo);
    // The top-level container never displays itself here — container-self
    // rendering (e.g. a zip that provenance types as an image) is handled by
    // the ``sourceFilePath`` / ``fileInfoFileUrl`` cascade below.
    const targetIsContainerSelf = !dispatchedFilePath && isContainer;
    if (USABLE_MEDIA_TYPES.includes(mediaType) && !targetIsContainerSelf) {
      // Prefer provenance's web-friendly preview, then the real storage
      // ``fileUrl``. Tree-picker / evidence-locker rows often use a virtual
      // searchable path as ``sourceUrl`` (``data/bulk/…/archive.zip/inner.png``)
      // which is not an S3 object — proxying it 404s. The processed extract
      // lives at ``fileInfo.fileUrl`` (``s3://…``). When ``displayTarget`` is
      // already an ``s3://`` URI (chat citations), keep it unless a distinct
      // preview/transcode exists.
      // Only let provenance preview/file URLs override when the file-info
      // record actually describes the same displayed object. Using a parent
      // container/email summary here can swap a cited attachment URL (e.g. PDF)
      // to unrelated bytes (e.g. email markdown body) and crash document viewers.
      const infoForDisplay = scopedFileInfo;
      const previewUrl =
        typeof infoForDisplay?.previewUrl === 'string' ? infoForDisplay.previewUrl.trim() : '';
      if (
        previewUrl &&
        previewUrl !== displayTarget &&
        !isArchiveMemberSearchablePath(previewUrl)
      ) {
        const previewUrlType = getMediaType(sourceLikeForPathOnly(source, previewUrl));
        if (!isTextProxyOfPagedDocument(mediaType, previewUrlType)) {
          return {
            mediaUrl: previewUrl,
            mediaType: mediaTypeForDerivedPreview(mediaType, previewUrlType),
            selectedBy: 'derivedPreview' as const,
          };
        }
      }
      const sourceS3Url =
        typeof (source as MediaSource).s3Url === 'string'
          ? (source as MediaSource).s3Url!.trim()
          : '';
      const fileUrlCandidates = [
        typeof infoForDisplay?.fileUrl === 'string' ? infoForDisplay.fileUrl.trim() : '',
        sourceS3Url,
      ].filter(
        (url) =>
          !!url &&
          url !== displayTarget &&
          !isArchiveMemberSearchablePath(url) &&
          (url.startsWith('s3://') || url.startsWith('files/') || /^https?:\/\//i.test(url)),
      );
      if (fileUrlCandidates.length > 0 && !displayTarget.startsWith('s3://')) {
        return {
          mediaUrl: fileUrlCandidates[0],
          mediaType,
          selectedBy: 'fileInfoFileUrl' as const,
        };
      }
      // Never proxy a virtual ``…/archive.zip/inner`` searchable path — it 404s.
      if (isArchiveMemberSearchablePath(displayTarget)) {
        // fall through to later branches / none
      } else {
        return {
          mediaUrl: displayTarget,
          mediaType,
          selectedBy: dispatchedFilePath ? ('dispatchedFilePath' as const) : ('sourceFilePath' as const),
        };
      }
    }

    // file-info may describe the parent archive while the row still carries the
    // extract's ``s3Url`` (virtual-path lookup). Prefer that playable URL.
    if (USABLE_MEDIA_TYPES.includes(mediaType) && targetIsContainerSelf) {
      const sourceS3Url =
        typeof (source as MediaSource).s3Url === 'string'
          ? (source as MediaSource).s3Url!.trim()
          : '';
      if (
        sourceS3Url &&
        !isArchiveMemberSearchablePath(sourceS3Url) &&
        (sourceS3Url.startsWith('s3://') || sourceS3Url.startsWith('files/')) &&
        !displayTarget.startsWith('s3://')
      ) {
        return {
          mediaUrl: sourceS3Url,
          mediaType,
          selectedBy: 'fileInfoFileUrl' as const,
        };
      }
    }
  }

  // Fallback: the static ``previewFilePath`` proxy (an image thumbnail / waveform
  // of an AV extract, or an untyped non-container preview). Only reached when the
  // displayed object above could not be typed to a usable media type (e.g.
  // file-info not loaded yet, or an unrecognised container extract). Container
  // parents carry multiple citation urls at once, so require a displayable type
  // for the preview path; non-container sources keep it even when ``unknown``
  // (e.g. proxy / junkdrawer urls). Never return a virtual archive-member path —
  // it is not a real S3 object and /api/proxy 404s (same gate as displayTarget).
  if (
    previewFilePath &&
    !isArchiveMemberSearchablePath(previewFilePath) &&
    (!isContainer || USABLE_MEDIA_TYPES.includes(previewType))
  ) {
    return {
      mediaUrl: previewFilePath,
      mediaType: previewType,
      selectedBy: 'previewFilePath' as const,
    };
  }

  const sourceInlinePreviewUrl = (() => {
    const s = source as Source & { preview_url?: string };
    const raw = s.previewUrl ?? s.preview_url ?? '';
    return typeof raw === 'string' ? raw.trim() : '';
  })();
  const mediaContextPreviewUrl = (() => {
    const raw = (source as MediaSource).mediaContext?.previewUrl;
    return typeof raw === 'string' ? raw.trim() : '';
  })();
  const derivedPreviewPath =
    (typeof source.derived_object_url === 'string' ? source.derived_object_url.trim() : '') ||
    (typeof fileInfo?.previewUrl === 'string' ? fileInfo.previewUrl.trim() : '') ||
    sourceInlinePreviewUrl ||
    mediaContextPreviewUrl;
  if (derivedPreviewPath && !isArchiveMemberSearchablePath(derivedPreviewPath)) {
    return {
      mediaUrl: derivedPreviewPath,
      mediaType: mediaTypeForPathWithFileInfo(source, fileInfo, derivedPreviewPath),
      selectedBy: 'derivedPreview' as const,
    };
  }

  const sourcePathMediaType = sourceFilePath
    ? mediaTypeForPathWithFileInfo(source, fileInfo, sourceFilePath)
    : 'unknown';
  const sourcePathMediaTypeNoInfo = sourceFilePath
    ? mediaTypeForPathWithoutFileInfo(source, sourceFilePath)
    : 'unknown';
  const isImageOnlyFromFileInfoFallback =
    sourcePathMediaTypeNoInfo === 'unknown' && sourcePathMediaType === 'image';
  const containerImageFallback =
    isImageOnlyFromFileInfoFallback && isContainerLikeSource(source, fileInfo);
  const isContainerSelfDisplay =
    !!sourceFilePath && isContainerLikeSource(source, fileInfo);

  if (sourceFilePath && !containerImageFallback && !isContainerSelfDisplay) {
    if (!isArchiveMemberSearchablePath(sourceFilePath)) {
      return {
        mediaUrl: sourceFilePath,
        mediaType: sourcePathMediaType,
        selectedBy: 'sourceFilePath' as const,
      };
    }
  }

  // Provenance file-info objects expose ``previewUrl`` (preferred above, via the
  // ``derivedPreview`` branch) and a canonical ``fileUrl``. These can arrive
  // nested on ``fileInfo`` (the common case) or top-level on the object itself
  // when a ``FileSummary`` is passed directly (evidence locker / file listings),
  // so fall back to either as the last resort after ``previewUrl``.
  const topLevelFileUrl =
    typeof (source as { fileUrl?: unknown }).fileUrl === 'string'
      ? (source as { fileUrl: string }).fileUrl.trim()
      : '';
  const fileInfoFileUrl =
    (typeof fileInfo?.fileUrl === 'string' ? fileInfo.fileUrl.trim() : '') || topLevelFileUrl;
  if (
    fileInfoFileUrl &&
    !isArchiveMemberSearchablePath(fileInfoFileUrl) &&
    !isContainerLikeSource(source, fileInfo)
  ) {
    return {
      mediaUrl: fileInfoFileUrl,
      mediaType: mediaTypeForPathWithFileInfo(source, fileInfo, fileInfoFileUrl),
      selectedBy: 'fileInfoFileUrl' as const,
    };
  }

  return {
    mediaUrl: undefined,
    mediaType: 'unknown' as const,
    selectedBy: 'none' as const,
  };
};

export const TRANSCODED_MEDIA_RETRY_NOTICE =
  'If this media does not work in your browser, please retry after cloud icon is green and the transcoded version is available.';

/**
 * True when provenance ``fileInfo`` (from source-info) is loaded, there is no
 * ``previewUrl`` yet, and the source is image/video/audio that may need a
 * browser-friendly transcode before it plays reliably (e.g. AVI without MP4).
 */
export function shouldShowTranscodedMediaRetryNotice(
  sourceUrl: string,
  fileSummary: FileSummary | null | undefined,
): boolean {
  if (!fileSummary) return false;

  const previewUrl =
    typeof fileSummary.previewUrl === 'string' ? fileSummary.previewUrl.trim() : '';
  if (previewUrl) return false;

  const source = { sourceFilePath: sourceUrl || fileSummary.fileUrl || '' } as Source;
  const preferred = resolvePreferredMediaDisplayForSource(source, fileSummary);

  if (!['image', 'video', 'audio'].includes(preferred.mediaType)) {
    return false;
  }

  if (!preferred.mediaUrl || preferred.mediaType === 'unknown') {
    return true;
  }

  if (!USABLE_MEDIA_TYPES.includes(preferred.mediaType)) {
    return true;
  }

  const sourceExt = extensionFromMediaPath(sourceUrl || fileSummary.fileUrl || '');
  const resolvedExt = extensionFromMediaPath(preferred.mediaUrl);

  if (
    preferred.mediaType === 'video' &&
    (NON_NATIVE_HTML5_VIDEO_EXTENSIONS.has(sourceExt) ||
      NON_NATIVE_HTML5_VIDEO_EXTENSIONS.has(resolvedExt))
  ) {
    return true;
  }

  if (
    preferred.mediaType === 'audio' &&
    (NON_NATIVE_HTML5_AUDIO_EXTENSIONS.has(sourceExt) ||
      NON_NATIVE_HTML5_AUDIO_EXTENSIONS.has(resolvedExt))
  ) {
    return true;
  }

  if (
    preferred.mediaType === 'image' &&
    (LIMITED_BROWSER_IMAGE_EXTENSIONS.has(sourceExt) ||
      LIMITED_BROWSER_IMAGE_EXTENSIONS.has(resolvedExt))
  ) {
    return true;
  }

  return false;
}

/**
 * Canonical citation page for a source or media object.
 * Prefer top-level `page` (string from chat/LLM), then `pageNumber`, then nested contexts.
 * SourceCard previously used only `.page` and merged `sourceContext`, which could leave a stale
 * `sourceContext.pageNumber` when switching between PDFs (e.g. still requesting page 185 on a ~100-page doc).
 */
type CitationPageFields = {
  page?: string | number;
  pageNumber?: number;
  mediaContext?: { pageNumber?: number };
  sourceContext?: { pageNumber?: number };
};

/** Page only when the row actually cites one. Missing values stay undefined — they are not page 1. */
export function explicitCitationPageFromSourceLike(
  obj: CitationPageFields | null | undefined,
): number | undefined {
  if (!obj) return undefined;
  const raw =
    obj.page ??
    obj.pageNumber ??
    obj.mediaContext?.pageNumber ??
    obj.sourceContext?.pageNumber;
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(n) && n >= 1 ? n : undefined;
}

export function parseCitationPageFromSourceLike(
  obj: CitationPageFields | null | undefined,
): number {
  return explicitCitationPageFromSourceLike(obj) ?? 1;
}

function citationPageFieldsFromMedia(
  media: MediaSource | undefined,
): CitationPageFields | undefined {
  if (!media) return undefined;
  const m = media as MediaSource & {
    page?: string | number;
    pageNumber?: number;
    sourceContext?: { pageNumber?: number };
  };
  return {
    page: m.page,
    pageNumber: m.pageNumber,
    mediaContext: media.mediaContext,
    sourceContext: m.sourceContext,
  };
}

/** Page shown on a tile only when the row cites one. ``parseCitationPageFromMedia`` defaults missing pages to 1. */
export function explicitCitationPageFromMedia(
  media: MediaSource | undefined,
): number | undefined {
  return explicitCitationPageFromSourceLike(citationPageFieldsFromMedia(media));
}

/** MediaSource from SourceCard includes `page` / `pageNumber` from the spread Source row. */
export function parseCitationPageFromMedia(media: MediaSource | undefined): number {
  return explicitCitationPageFromMedia(media) ?? 1;
}

export const decodeCongnitoToken = (jwtToken: string): any => {
  try {
    const parts = jwtToken.split('.');
    if (parts.length !== 3) return null;
    const decoded1 = atob(parts[1]);
    return JSON.parse(decoded1);
  } catch (error) {
    try {
      const decoded2 = jwtDecode(jwtToken);
      return decoded2;
    } catch (error) {
      return null;
    }
  }
};

export const getTokenInfo = (jwtToken: string): any => {
  try {
    const tokenInfo1 = jwtDecode(jwtToken);
    return tokenInfo1;
  } catch (error) {
    try {
      const tokenInfo2 = decodeCongnitoToken(jwtToken);
      return tokenInfo2;
    } catch (error) {
      return null;
    }
  }
}

const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
};

// Helper functions to extract user information from Cognito tokens.
export const getUserId = (tokenInfo: any): string => {
  if (!tokenInfo) return '';
  return tokenInfo.sub ?? '';
}

export const getUserEmail = (tokenInfo: any): string => {
  if (!tokenInfo) return '';
  return tokenInfo.email ?? '';
}

export const getUserEmailForHeaders = (idTokenInfo: any, _accessTokenInfo: any): string => {
  if (!idTokenInfo) return '';
  return idTokenInfo.email ?? '';
}

export const getUsername = (tokenInfo: any): string => {
  if (!tokenInfo) return '';
  return tokenInfo['cognito:username'] || tokenInfo.username || '';
}

export const validateUserInfo = (id: any, access: any): void => {
  const email = getUserEmailForHeaders(id, access);
  const username = getUsername(id);
  const userId = getUserId(id);

  if (!email || email === '') {
    throw new Error('No email found in tokens');
  }
  if (!username || username === '') {
    throw new Error('No username found in tokens');
  }
  if (!userId || userId === '') {
    throw new Error('No user ID found in tokens');
  }

}

// Helper for decodeToken results (used in frontend components)
export const getUserEmailFromDecodedToken = (decodedToken: any): string => {
  if (!decodedToken) return '';
  return decodedToken.email ?? '';
}

export const getUserGroups = (tokenInfo: any): string => {
  if (!tokenInfo) return 'admin,user';
  const groups = tokenInfo['cognito:groups'];
  if (Array.isArray(groups) && groups.length > 0) {
    return groups.join(',');
  }
  return 'admin,user';
}

/**
 * Parse bare seconds or legacy clock strings (``M:SS`` / ``H:MM:SS``) to seconds.
 * Shared by the player, trim handles, and Selected Sources membership / dedupe.
 */
export const getSecondsFromTimeString = (timeString: string | number): number => {
  if (typeof timeString === 'number') {
    return Number.isFinite(timeString) ? timeString : 0;
  }
  const trimmed = timeString.trim();
  if (!trimmed) return 0;
  const timeStringArray = trimmed.split(':');
  if (timeStringArray.length === 1) {
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) ? asNumber : 0;
  }
  if (timeStringArray.length === 2) {
    const [minutes, seconds] = timeStringArray.map(Number);
    if (!Number.isNaN(minutes) && !Number.isNaN(seconds)) {
      return minutes * 60 + seconds;
    }
  } else if (timeStringArray.length === 3) {
    const [hours, minutes, seconds] = timeStringArray.map(Number);
    if (
      !Number.isNaN(hours) &&
      !Number.isNaN(minutes) &&
      !Number.isNaN(seconds)
    ) {
      return hours * 3600 + minutes * 60 + seconds;
    }
  }
  return 0;
};

/**
 * True when start/end means unset / full-file. Matches the player, media
 * thumbnail labels, and membership compares — including legacy clock zeros
 * (``"0:00"`` / ``"0:00:00"``) that normalize to 0 seconds.
 */
export const isUnsetMediaTrimTime = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (value === 0 || value === '0' || value === '0.0') return true;
  if (typeof value === 'number' && Number.isFinite(value)) return value === 0;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '0' || trimmed === '0.0') return true;
    const parts = trimmed.split(':');
    if (parts.length === 1) {
      const asNumber = Number(parts[0]);
      return Number.isFinite(asNumber) && asNumber === 0;
    }
    const seconds = getSecondsFromTimeString(trimmed);
    return Number.isFinite(seconds) && seconds === 0;
  }
  return false;
};

/**
 * The path a media caption should name — the file the user picked, not the
 * rendition being drawn for it.
 *
 * ``mediaUrl`` holds whatever is displayable, which for a moderated image is the
 * obfuscated copy (``…_obfuscated.jpg``) and for a PDF the markdown conversion.
 * Captioning that renames the user's file after an artifact of how we render it,
 * so prefer the canonical source path: the top-level pair while the row is still
 * in memory, then the ``mediaContext`` mirror that survives a save (the only
 * media field timeline-service round-trips), and only then the rendition.
 */
export const mediaCaptionPath = (media: MediaSource): string =>
  [
    media.sourceFilePath,
    media.sourceUrl,
    typeof media.mediaContext?.sourceFilePath === 'string'
      ? media.mediaContext.sourceFilePath
      : undefined,
    typeof media.mediaContext?.sourceUrl === 'string'
      ? media.mediaContext.sourceUrl
      : undefined,
    media.mediaUrl,
    media.mediaSrc,
  ].find((path) => typeof path === 'string' && path.trim()) ?? '';

/**
 * Identity path for the fullscreen media viewer header — the file the user
 * opened, not a rendition URL (``srcToUse`` / proxy / transcode).
 */
export function mediaPaneIdentityPath(media: MediaSource | undefined): string {
  if (!media) return '';
  const searchable =
    typeof media.fileInfo?.searchablePath === 'string' ? media.fileInfo.searchablePath : '';
  return (
    [
      media.truncatedFilePath,
      media.sourceFilePath,
      media.sourceUrl,
      searchable,
      mediaCaptionPath(media),
    ].find((path) => typeof path === 'string' && path.trim()) ?? ''
  );
}

/** File name shown in the fullscreen viewer header. Full identity stays on hover. */
export function mediaPaneTitleLabel(media: MediaSource | undefined): string {
  const identity = mediaPaneIdentityPath(media);
  const stripped = extractPathAfterBulk(identity, true).truncatedFilePath || identity;
  const basename = basenameFromStoragePath(stripped);
  return basename || 'Media';
}

/**
 * Normalize start/end for media-membership compares and dedupe keys.
 * Bare seconds and legacy clock strings (``M:SS`` / ``H:MM:SS``) become the
 * same numeric seconds so older saved trims still match.
 */
export const normalizeMediaMembershipTime = (
  value: unknown,
): number | string | null => {
  if (isUnsetMediaTrimTime(value)) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(':');
    if (parts.length === 1) {
      const asNumber = Number(parts[0]);
      return Number.isFinite(asNumber) ? asNumber : trimmed;
    }
    const seconds = getSecondsFromTimeString(trimmed);
    return Number.isFinite(seconds) ? seconds : trimmed;
  }
  return null;
};

/** Merge source + media context (mediaContext wins on key conflicts). */
export const mergeSourceAnnotationContext = (
  item: Pick<MediaSource, 'sourceContext' | 'mediaContext'>,
): Record<string, unknown> => ({
  ...(item.sourceContext || {}),
  ...(item.mediaContext || {}),
});

const SOURCE_CONTEXT_IDENTITY_EXCLUDE_KEYS = new Set([
  'lastUpdated',
  'previewUrl',
  // Which file the row renders, not how it is annotated. A media row persists
  // these so reload can pair it back to its Displayed Sources row; counting them
  // here would make a saved media row and its source fingerprint differently and
  // stop them deduping as one item.
  'sourceUrl',
  'sourceFilePath',
  'showTranscript',
  'showTranscriptMarkers',
  'autoScroll',
  'transcriptPosition',
]);

const SOURCE_CONTEXT_TIME_KEYS = new Set([
  'startTime',
  'endTime',
  'originalStartTime',
  'originalEndTime',
]);

const normalizeContextStorageRef = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:')) {
    return `data:${trimmed.length}:${trimmed.slice(0, 64)}`;
  }
  const queryIdx = trimmed.indexOf('?');
  return queryIdx >= 0 ? trimmed.slice(0, queryIdx) : trimmed;
};

const normalizeCanvasContextForIdentity = (
  canvas: unknown,
): Record<string, unknown> | undefined => {
  if (!canvas || typeof canvas !== 'object' || Array.isArray(canvas)) {
    return undefined;
  }
  const input = canvas as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of ['pageNumber', 'page', 'rotation']) {
    if (input[key] !== undefined && input[key] !== null && input[key] !== '') {
      out[key] = input[key];
    }
  }
  for (const imageKey of ['image', 'stillFrameImage']) {
    const ref = input[imageKey];
    if (typeof ref === 'string' && ref.trim()) {
      out[imageKey] = normalizeContextStorageRef(ref);
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const normalizeContextFieldForIdentity = (
  key: string,
  value: unknown,
): unknown => {
  if (value === undefined || value === null || value === '') return undefined;
  if (SOURCE_CONTEXT_TIME_KEYS.has(key)) {
    return normalizeMediaMembershipTime(value);
  }
  if (key === 'stillFrame') {
    return typeof value === 'string'
      ? normalizeContextStorageRef(value)
      : value;
  }
  if (key === 'canvas') {
    return normalizeCanvasContextForIdentity(value);
  }
  if (key === 'transcriptUrl' || key === 'transcript_url') {
    return typeof value === 'string'
      ? normalizeContextStorageRef(value)
      : value;
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

/** Normalize one context object for stable list identity comparisons. */
export const normalizeContextForIdentity = (
  context: SourceContext | Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  if (!context || typeof context !== 'object') return normalized;
  for (const key of Object.keys(context).sort()) {
    if (SOURCE_CONTEXT_IDENTITY_EXCLUDE_KEYS.has(key)) continue;
    const normalizedValue = normalizeContextFieldForIdentity(key, context[key]);
    if (normalizedValue === undefined || normalizedValue === null) continue;
    if (SOURCE_CONTEXT_TIME_KEYS.has(key) && normalizedValue === null) {
      continue;
    }
    normalized[key] = normalizedValue;
  }
  const rawPage = normalized.page ?? normalized.pageNumber;
  delete normalized.page;
  delete normalized.pageNumber;
  if (rawPage !== undefined && rawPage !== null && rawPage !== '') {
    const asNum = Number(rawPage);
    if (Number.isFinite(asNum) && asNum > 0) {
      normalized.pageNumber = asNum;
    } else if (typeof rawPage === 'string' && rawPage.trim()) {
      normalized.pageNumber = rawPage.trim();
    }
  }
  return normalized;
};

const attachTopLevelPageToSourceContext = (
  sourceContext: Record<string, unknown>,
  pageNumber: number | undefined,
): Record<string, unknown> => {
  if (
    pageNumber != null &&
    sourceContext.pageNumber === undefined &&
    sourceContext.page === undefined
  ) {
    return { ...sourceContext, pageNumber };
  }
  return sourceContext;
};

/**
 * Stable fingerprint of ``sourceContext`` + ``mediaContext`` for dedup and pairing.
 * Both fields must match for two rows to be the same item.
 */
export const getSourceMediaContextPairFingerprint = (
  item: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'>,
): string => {
  const sourceContext = attachTopLevelPageToSourceContext(
    normalizeContextForIdentity(item.sourceContext),
    item.pageNumber,
  );
  const mediaContext = normalizeContextForIdentity(item.mediaContext);
  return JSON.stringify({ sourceContext, mediaContext });
};

/**
 * Canonical pair for list identity keys: when only one context is populated,
 * mirror it so source-only and synced rows dedupe together.
 */
export const getCanonicalContextPairFingerprint = (
  item: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'>,
): string => {
  let sourceContext = attachTopLevelPageToSourceContext(
    normalizeContextForIdentity(item.sourceContext),
    item.pageNumber,
  );
  let mediaContext = normalizeContextForIdentity(item.mediaContext);
  const sourceEmpty = Object.keys(sourceContext).length === 0;
  const mediaEmpty = Object.keys(mediaContext).length === 0;
  if (sourceEmpty && !mediaEmpty) {
    sourceContext = { ...mediaContext };
  } else if (!sourceEmpty && mediaEmpty) {
    mediaContext = { ...sourceContext };
  }
  return JSON.stringify({ sourceContext, mediaContext });
};

export const itemsShareSameContext = (
  a: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'>,
  b: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'>,
): boolean =>
  getSourceMediaContextPairFingerprint(a) === getSourceMediaContextPairFingerprint(b);

/** Citation context on a Selected Sources row (includes top-level page when set). */
export const normalizeSourceRowContextForMediaPairing = (
  source: Pick<MediaSource, 'sourceContext' | 'pageNumber'> & {
    page?: string | number;
  },
): Record<string, unknown> => {
  const topLevelPage =
    source.pageNumber ??
    (source.page != null && source.page !== '' ? source.page : undefined);
  const pageNum =
    topLevelPage != null && topLevelPage !== ''
      ? Number(topLevelPage)
      : undefined;
  return attachTopLevelPageToSourceContext(
    normalizeContextForIdentity(source.sourceContext),
    pageNum != null && Number.isFinite(pageNum) && pageNum > 0
      ? pageNum
      : undefined,
  );
};

/** Citation context on a Selected Media row (trim, page, still frame, etc.). */
export const normalizeMediaRowContextForPairing = (
  media: Pick<MediaSource, 'mediaContext'>,
): Record<string, unknown> => normalizeContextForIdentity(media.mediaContext);

/** Citation fields that define Selected Sources ↔ Selected Media pairing. */
const SOURCE_MEDIA_PAIRING_CITATION_KEYS = new Set([
  'startTime',
  'endTime',
  'pageNumber',
  'page',
  'rotation',
  'capture',
]);

/** Trim/page/capture only — omit player UI, file metadata, canvas/stillFrame blobs. */
const pickCitationContextForSourceMediaPairing = (
  ctx: Record<string, unknown>,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of SOURCE_MEDIA_PAIRING_CITATION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(ctx, key)) {
      out[key] = ctx[key];
    }
  }
  return out;
};

/**
 * Citation fingerprint stable whether trim/page live on ``sourceContext``,
 * legacy ``source.mediaContext``, or both before hydration sync reshapes them.
 */
export const getStableSourceCitationFingerprint = (
  source: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'> & {
    page?: string | number;
  },
): string => {
  const fromSource = pickCitationContextForSourceMediaPairing(
    normalizeSourceRowContextForMediaPairing(source),
  );
  const fromMedia = pickCitationContextForSourceMediaPairing(
    normalizeMediaRowContextForPairing(source),
  );
  return JSON.stringify({ ...fromSource, ...fromMedia });
};

/** Citation-only slice of ``media.mediaContext`` safe to overlay onto ``source.sourceContext``. */
export const getMediaCitationFieldsForSourceSync = (
  mediaContext: Record<string, unknown> | undefined,
): Record<string, unknown> =>
  pickCitationContextForSourceMediaPairing(
    normalizeMediaRowContextForPairing({ mediaContext }),
  );

/** True when media carries citation keys worth syncing onto a paired source row. */
export const shouldSyncMediaCitationOntoSource = (
  mediaContext: Record<string, unknown> | undefined,
): boolean => {
  if (!mediaContext || typeof mediaContext !== 'object') return false;
  if (Object.keys(getMediaCitationFieldsForSourceSync(mediaContext)).length > 0) {
    return true;
  }
  return [...SOURCE_MEDIA_PAIRING_CITATION_KEYS].some((key) =>
    Object.prototype.hasOwnProperty.call(mediaContext, key),
  );
};

/** Merge media citation fields onto an existing source context without wiping source-only keys. */
export const mergeMediaCitationContextOntoSourceContext = (
  sourceContext: Record<string, unknown> | undefined,
  mediaContext: Record<string, unknown> | undefined,
): Record<string, unknown> => {
  const existing =
    sourceContext && typeof sourceContext === 'object' ? { ...sourceContext } : {};
  if (!shouldSyncMediaCitationOntoSource(mediaContext)) {
    return existing;
  }
  const rawMedia =
    mediaContext && typeof mediaContext === 'object' ? mediaContext : {};
  const normalizedCitation = getMediaCitationFieldsForSourceSync(mediaContext);
  for (const key of SOURCE_MEDIA_PAIRING_CITATION_KEYS) {
    if (Object.prototype.hasOwnProperty.call(normalizedCitation, key)) {
      existing[key] = normalizedCitation[key];
    } else if (Object.prototype.hasOwnProperty.call(rawMedia, key)) {
      delete existing[key];
    }
  }
  return existing;
};

/**
 * True when a Selected Sources row corresponds to a Selected Media row:
 * same file identity (checked separately) and ``source.sourceContext`` equals
 * ``media.mediaContext`` on citation fields (trim, page, rotation, capture).
 * Player-only state (``canvas``, ``stillFrame``, transcript UI, stream metadata)
 * does not affect pairing. Empty citation contexts match only other empty ones.
 */
export const sourceContextMatchesMediaContext = (
  source: Pick<MediaSource, 'sourceContext' | 'pageNumber'> & {
    page?: string | number;
  },
  media: Pick<MediaSource, 'mediaContext'>,
): boolean =>
  JSON.stringify(
    pickCitationContextForSourceMediaPairing(
      normalizeSourceRowContextForMediaPairing(source),
    ),
  ) ===
  JSON.stringify(
    pickCitationContextForSourceMediaPairing(
      normalizeMediaRowContextForPairing(media),
    ),
  );

/**
 * True when two rows in the same list (both sources or both media) share the
 * same context pair fingerprint.
 */
export const itemsShareEquivalentContext = (
  a: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'>,
  b: Pick<MediaSource, 'sourceContext' | 'mediaContext' | 'pageNumber'>,
): boolean => itemsShareSameContext(a, b);

/** @deprecated alias — use ``getSourceMediaContextPairFingerprint`` */
export const getSourceContextIdentityFingerprint = getSourceMediaContextPairFingerprint;

/**
 * Resolve a trim handle to seconds. ``0`` / ``"0"`` / ``"0:00"`` mean unset and
 * use ``fallback`` (start→0, end→duration).
 */
export const resolveMediaTrimHandleSeconds = (
  value: unknown,
  fallback: number,
): number => {
  if (isUnsetMediaTrimTime(value)) return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    const seconds = getSecondsFromTimeString(trimmed);
    return seconds === 0 ? fallback : seconds;
  }
  return fallback;
};

/**
 * Format seconds to ``H:MM:SS`` / ``M:SS`` / ``0:SS`` for the media player.
 * Pass ``fractionDigits`` (e.g. 3) to include fractional seconds so frame-accurate
 * seeks (``video.currentTime`` is a float) are visible in the UI.
 */
export const formatTimeIndex = (timeStr: string, fractionDigits = 0) => {
  if (timeStr == null || timeStr === '') return '';
  const raw = timeStr.toString();
  // Preserve already-formatted clock strings when not requesting fractions
  if (raw.indexOf(':') !== -1 && fractionDigits <= 0) return raw;
  let time = parseFloat(raw);
  if (isNaN(time)) return '';
  if (time < 0) time = 0;
  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60);
  const secondsTotal = time % 60;
  const digits = Math.max(0, Math.min(3, Math.floor(fractionDigits)));
  const secondsStr =
    digits > 0
      ? secondsTotal.toFixed(digits).padStart(2 + 1 + digits, '0')
      : Math.floor(secondsTotal).toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secondsStr}`;
  }
  return `${minutes}:${secondsStr}`;
};

/** Pull a `case:<id>` value out of the provenance ``tags`` array (case-insensitive prefix). */
export function caseIdFromTags(tags: string[] | null | undefined): string | undefined {
  if (!Array.isArray(tags)) return undefined;
  for (const t of tags) {
    if (typeof t !== 'string') continue;
    const m = t.match(/^case:(.+)$/i);
    if (m && m[1]) return m[1].trim();
  }
  return undefined;
}

/** True when the file's streams (or mime type, as a fallback) indicate audio/video. */
export function isAudioVideoFileInfo(fi: FileSummary | null | undefined): boolean {
  if (!fi) return false;
  if (fi.hasAudioStream === true || fi.hasVideoStream === true) return true;
  const mime = (fi.mimeType || '').toLowerCase();
  return mime.startsWith('audio/') || mime.startsWith('video/');
}

/**
 * Deterministic UUID (v5, URL namespace) for a URL, matching the backend's
 * ``source_url_uuid`` derivation: hyphens stripped and lowercased. Used to key
 * a source file by its S3 URL (e.g. the junkdrawer email-search
 * ``sourceUrlUuid`` param computed from the root source URL).
 */
export function getUrlUuid(url: string): string {
  return uuidv5(url, uuidv5.URL).replace(/-/g, '').toLowerCase();
}

/**
 * Project a provenance ``/v1/files/file-info`` envelope into the canonical
 * ``FileSummary`` shape. The endpoint returns a discriminated union on
 * ``kind``: ``{ kind: 'file', file }`` is already a ``FileSummary`` so we
 * pass it through; ``{ kind: 'subdocument', subdocument }`` carries a
 * narrower set of fields, so we widen it into ``FileSummary`` with
 * subdoc-incompatible fields set to ``null`` / ``undefined`` / their
 * documented "no info" sentinel. Downstream helpers (``getMediaType``,
 * ``isContainerLikeSource``, …) fall back to mime / extension based
 * inference when the file-only fields are null.
 */
export const fileInfoFromProvenanceBody = (
  payload: ProvenanceFileInfoBody | null | undefined,
): FileSummary | null => {
  if (!payload) return null;

  if (payload.kind === 'file') {
    return payload.file ?? null;
  }

  if (payload.kind === 'subdocument') {
    const sub: ProvenanceSubdocumentInfo | undefined = payload.subdocument;
    if (!sub) return null;
    // Treat ``subdocument`` as the authoritative record for this URL — same
    // fields consumers read from a top-level ``file`` response (``previewUrl``,
    // ``fileUrl``, mime/extension, AV flags, …).
    return {
      createdAt: sub.createdAt,
      updatedAt: sub.updatedAt,
      id: sub.id,
      // Subdocuments belong to a root file but don't carry the parent's
      // ``fileStatusId`` over the wire; ``rootId`` is the closest analogue.
      fileStatusId: sub.rootId,
      fileUuid: sub.fileUuid,
      searchablePath: sub.searchablePath,
      fileName: sub.fileName,
      fileUrl: sub.fileUrl,
      tags: [],
      // Subdocuments inherit the enclosing container's content category as
      // lineage (an image extracted from a zip arrives as
      // ``["archive", "image"]``). That lineage tag would make every extracted
      // file read as a container — suppressing the preview affordance and the
      // SourceCard media display — so strip it here. The subdocument's own
      // ``mimeType`` / ``extension`` still classify genuine nested containers
      // (zip, tar, …) via ``isContainerLikeSource``.
      contentCategories: stripContainerLineageCategories(sub.contentCategories),
      status: sub.status,
      statusMessage: sub.statusMessage ?? '',
      subdocumentCount: 0,
      subdocumentSuccessCount: 0,
      subdocumentFailureCount: 0,
      subdocumentSkipCount: 0,
      countersLastUpdatedAt: '',
      lastUpdateSentAt: '',
      md5: sub.md5,
      blake3: sub.blake3 ?? '',
      xxhash: sub.xxhash ?? '',
      size: sub.size,
      mimeType: sub.mimeType,
      extension: sub.extension,
      height: sub.height,
      width: sub.width,
      format: sub.format,
      durationInSeconds: sub.durationInSeconds,
      pageNumber: sub.pageNumber,
      pageCount: sub.pageCount,
      hasAudioStream: sub.hasAudioStream,
      hasVideoStream: sub.hasVideoStream,
      hasTranscript: sub.hasTranscript,
      isImage: sub.isImage,
      previewUrl: sub.previewUrl,
      downloadUrl: sub.downloadUrl,
      transcriptUrl: sub.transcriptUrl,
      // Subdocuments are moderated independently of their container, so the
      // backend now carries per-subdocument content-moderation metadata —
      // pass it through so extracted files (e.g. images inside an archive)
      // are flagged, gated and obfuscated just like top-level files.
      contentModeration: sub.contentModeration ?? null,
    };
  }

  return null;
};

/**
 * Fetch file info for a given S3-style ``sourceFilePath`` via direct CORS to the
 * cell provenance-service.
 *
 * Calls ``GET /provenance-service/v1/files/file-info?url=<s3 url>`` on
 * ``https://{cellId}.api.tranquility.ai`` (via ``fetchProvenanceFileInfo``) and
 * projects the response into the canonical ``FileSummary`` shape.
 *
 * ``sourceFilePath`` may be a full ``s3://<bucket>/<key>`` URL or just a relative key.
 * When it's a relative key we pass it through unchanged — upstream provenance still
 * indexes by full URL, so the relative-key case may return an error.
 *
 * ``cellId`` and ``accessToken`` come from ``useCellServiceSession`` at the call site;
 * if either is missing this returns ``null`` (mirrors the legacy behavior where an
 * unauthenticated/unresolved session also produced no metadata).
 */
/**
 * Like {@link getFileInfoFromServer} but returns the raw discriminated
 * ``ProvenanceFileInfoBody`` envelope instead of the projected ``FileSummary``.
 *
 * The projection in {@link fileInfoFromProvenanceBody} drops the ``hierarchy``
 * breadcrumb (root source file → enclosing subdocs → target); callers that
 * need it — e.g. the SourceCard download dropdown — use this and keep the
 * envelope around.
 */
export const getFileInfoBodyFromServer = async (
  sourceFilePath: string,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
  bypassCm?: boolean,
): Promise<ProvenanceFileInfoBody | null> => {
  try {
    if (!sourceFilePath || sourceFilePath.trim() === '') {
      return null;
    }
    if (!cellId || !accessToken) {
      return null;
    }
    // Virtual ``data/bulk/…/archive.zip/inner.png`` paths are not S3 objects.
    // Provenance returns ``file not found for the given s3 URL`` — skip the
    // round-trip. Callers must use the extract's processed ``fileUrl`` (from
    // the tree node) or ``GET /tree?fileStatusId=…`` instead.
    if (isArchiveMemberSearchablePath(sourceFilePath)) {
      return null;
    }
    const lookupPath = toProvenanceLookupPath(sourceFilePath);
    if (isArchiveMemberSearchablePath(lookupPath)) {
      return null;
    }
    return await fetchProvenanceFileInfo({
      cellId,
      accessToken,
      s3Url: lookupPath,
      bypassCm,
    });
  } catch {
    return null;
  }
}

export const getFileInfoFromServer = async (
  sourceFilePath: string,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
  bypassCm?: boolean,
): Promise<FileSummary | null> => {
  const body = await getFileInfoBodyFromServer(sourceFilePath, cellId, accessToken, bypassCm);
  return fileInfoFromProvenanceBody(body);
}

/** Provenance rejects a batch larger than this (``BatchFileInfoRequest``). */
const FILE_INFO_BATCH_MAX_URLS = 500;

const isBatchFileInfoEnvelope = (
  body: unknown,
): body is { results: Record<string, ProvenanceFileInfoBody | null | undefined> } => {
  if (!body || typeof body !== 'object') return false;
  const results = (body as { results?: unknown }).results;
  return !!results && typeof results === 'object' && !Array.isArray(results);
};

/**
 * True when the batch route rejected the call because one submitted URL is
 * bad. That status is fail-fast for the whole list, so the caller splits
 * until the bad URL is isolated. A throttle or outage is a different status
 * and must not be split into more requests.
 *
 * A 404 whose body is not one of these lookup failures is a missing route,
 * not a missing file — splitting that would turn one 404 into hundreds.
 */
const isSplittableFileInfoBatchError = (error: unknown): boolean => {
  if (!(error instanceof ProvenanceApiError)) return false;
  if (error.status !== 400 && error.status !== 404) return false;
  const detail = `${error.detail ?? ''} ${error.message}`.toLowerCase();
  return (
    detail.includes('file not found') ||
    detail.includes('subdocument not found') ||
    detail.includes('bucket') ||
    detail.includes('s3://') ||
    detail.includes('url must')
  );
};

/** The batch route is not on this cell (or the method is not allowed). */
const isBatchRouteUnavailable = (error: unknown): boolean => {
  if (!(error instanceof ProvenanceApiError)) return false;
  if (error.status === 405) return true;
  return error.status === 404 && !isSplittableFileInfoBatchError(error);
};

const provenanceLookupUrl = (sourceFilePath: string): string | null => {
  if (!sourceFilePath || sourceFilePath.trim() === '') return null;
  if (isArchiveMemberSearchablePath(sourceFilePath)) return null;
  const lookupPath = toProvenanceLookupPath(sourceFilePath);
  if (!lookupPath || isArchiveMemberSearchablePath(lookupPath)) return null;
  return lookupPath;
};

/**
 * Resolve one chunk via ``POST /files/file-info``.
 *
 * Returns ``null`` when the response is not a batch envelope (the route is
 * absent, or a mock answered with the single-URL GET body). Returns a map
 * keyed by the submitted lookup URL otherwise. A fail-fast 400/404 is split
 * so one missing file does not drop the rest of the chunk; any other error
 * degrades that chunk to null summaries.
 */
const resolveFileInfoLookupChunk = async (
  lookups: string[],
  cellId: string,
  accessToken: string,
): Promise<Map<string, FileSummary | null> | null> => {
  if (lookups.length === 0) return new Map();
  try {
    const body = await fetchProvenanceFileInfoBatch({
      cellId,
      accessToken,
      s3Urls: lookups,
    });
    if (!isBatchFileInfoEnvelope(body)) return null;
    const map = new Map<string, FileSummary | null>();
    for (const lookup of lookups) {
      map.set(lookup, fileInfoFromProvenanceBody(body.results[lookup] ?? null));
    }
    return map;
  } catch (error) {
    if (lookups.length > 1 && isSplittableFileInfoBatchError(error)) {
      const mid = Math.ceil(lookups.length / 2);
      const left = await resolveFileInfoLookupChunk(lookups.slice(0, mid), cellId, accessToken);
      const right = await resolveFileInfoLookupChunk(lookups.slice(mid), cellId, accessToken);
      if (!left || !right) return null;
      return new Map([...left, ...right]);
    }
    if (lookups.length === 1 && isSplittableFileInfoBatchError(error)) {
      return new Map([[lookups[0], null]]);
    }
    if (isBatchRouteUnavailable(error)) return null;
    return new Map(lookups.map((lookup) => [lookup, null]));
  }
};

/**
 * Fetch file info for many storage paths in as few provenance calls as possible.
 *
 * Uses ``POST /provenance-service/v1/files/file-info`` with ``{ urls }`` (chunks
 * of 500). A list of files must use this, not one ``GET /files/file-info`` per
 * file. Returns a map keyed by the **caller's** path, or ``null`` when the
 * response is not a batch envelope so the caller can fall back to per-URL GET.
 * That fallback is only for a missing route — do not skip this POST. Does not
 * request ``bypass_cm`` — the batch route ignores it, so that reveal stays a
 * single uncached GET.
 *
 * A missing session returns an all-null map (same as {@link getFileInfoFromServer})
 * rather than ``null``, because there is nothing to fall back to.
 */
export const getFileInfoBatchFromServer = async (
  sourceFilePaths: string[],
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<Map<string, FileSummary | null> | null> => {
  const result = new Map<string, FileSummary | null>();
  if (sourceFilePaths.length === 0) return result;
  if (!cellId || !accessToken) {
    for (const path of sourceFilePaths) result.set(path, null);
    return result;
  }

  const lookups: string[] = [];
  const seen = new Set<string>();
  for (const path of sourceFilePaths) {
    const lookup = provenanceLookupUrl(path);
    if (lookup && !seen.has(lookup)) {
      seen.add(lookup);
      lookups.push(lookup);
    }
  }

  const byLookup = new Map<string, FileSummary | null>();
  for (let i = 0; i < lookups.length; i += FILE_INFO_BATCH_MAX_URLS) {
    const chunk = lookups.slice(i, i + FILE_INFO_BATCH_MAX_URLS);
    const resolved = await resolveFileInfoLookupChunk(chunk, cellId, accessToken);
    if (!resolved) {
      if (byLookup.size === 0) return null;
      for (const lookup of chunk) byLookup.set(lookup, null);
      continue;
    }
    for (const [lookup, summary] of resolved) byLookup.set(lookup, summary);
  }

  for (const path of sourceFilePaths) {
    const lookup = provenanceLookupUrl(path);
    result.set(path, lookup ? (byLookup.get(lookup) ?? null) : null);
  }
  return result;
};

const getMatchingSources = (sources: Source[], date: string) => {
  const matchingSources: MediaSource[] = [];
  sources.forEach((source) => {
    if (source.content) {
      const extractedDates = extractDate(source.content, {
        direction: 'MD',
        locale: 'en',
        timeZone: 'America/New_York',
      });
      if (extractedDates && extractedDates.length > 0) {
        for (const extractedDate of extractedDates) {
          const extractedDateParts = extractedDate.date.split('-');
          const dateParts = date.split('-');
          const extractedDateIn = `${extractedDateParts[1]}-${extractedDateParts[2]}`;
          const dateIn = `${dateParts[1]}-${dateParts[2]}`;
          if (extractedDateIn === dateIn) {
            matchingSources.push(source as MediaSource);
          }
        }
      }
    }
  });
  return matchingSources;
}

export const getFirstStartTime = (transcript: string): string | null => {
  try {
    // Look for the first "start": "X:XX" pattern
    const startTimeMatch = transcript.match(/"start":\s*"(\d+:\d+)"/);
    if (startTimeMatch && startTimeMatch[1]) {
      return startTimeMatch[1];
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Transcript artifacts from the new pipeline are JSON files ending in ``.json``. Media
 * extracted from a container (e.g. a video attached to an ``.mbox`` email) instead gets its
 * transcript written alongside it as ``<filename>.transcript.txt`` — still JSON *content*
 * (speaker/segment/start/end), just not a ``.json``-suffixed key (TR-536). Accept both shapes
 * here so file-info / sourceContext transcript URLs for that pipeline aren't silently dropped.
 * Only URLs from provenance ``file-info`` (``file.transcriptUrl``), chat citations
 * (``sourceContext``), or timeline media (``mediaContext`` / ``sourceContext``) should be
 * treated as transcripts.
 */
export const isTranscriptJsonLocation = (
  location: string | null | undefined,
): location is string => {
  if (typeof location !== 'string' || location.length === 0) return false;
  const lower = location.toLowerCase();
  return lower.endsWith('.json') || lower.endsWith('.transcript.txt');
};

/** Resolve a transcript URL from ``sourceContext`` / ``mediaContext`` (``transcriptUrl`` or ``transcript_url``). */
export const resolveTranscriptUrlFromSourceContext = (
  ctx: Record<string, unknown> | null | undefined,
): string | undefined => {
  if (!ctx) return undefined;
  const fromNormalized =
    typeof ctx.transcriptUrl === 'string' && ctx.transcriptUrl.length > 0
      ? ctx.transcriptUrl
      : undefined;
  const fromRaw =
    typeof ctx.transcript_url === 'string' && ctx.transcript_url.length > 0
      ? ctx.transcript_url
      : undefined;
  if (isTranscriptJsonLocation(fromNormalized)) return fromNormalized;
  if (isTranscriptJsonLocation(fromRaw)) return fromRaw;
  return undefined;
};

/** Resolve a transcript URL from provenance ``file-info`` (``FileSummary.transcriptUrl``). */
export const resolveTranscriptUrlFromFileInfo = (
  fileInfo: Pick<FileSummary, 'transcriptUrl'> | null | undefined,
): string | undefined => {
  const candidate = fileInfo?.transcriptUrl;
  return isTranscriptJsonLocation(candidate) ? candidate : undefined;
};

/** Resolve the transcript URL for a media/timeline display item from its allowed fields only. */
export const resolveMediaTranscriptUrl = (
  item:
    | {
        transcriptUrl?: string | null;
        mediaContext?: Record<string, unknown> | null;
        fileInfo?: Pick<FileSummary, 'transcriptUrl'> | null;
      }
    | null
    | undefined,
): string | undefined => {
  if (!item) return undefined;
  return (
    resolveTranscriptUrlFromSourceContext(item.mediaContext) ??
    resolveTranscriptUrlFromSourceContext({ transcriptUrl: item.transcriptUrl }) ??
    resolveTranscriptUrlFromFileInfo(item.fileInfo)
  );
};

/**
 * When a resolvable transcript JSON URL is found, paint the timeline player
 * affordances (``showTranscript``, markers) onto ``mediaContext``.
 * ``transcriptPosition`` is only forwarded when already explicit — callers
 * supply per-surface defaults via {@link resolveTranscriptPosition} (e.g.
 * below for SourceCard inline, beside for fullscreen).
 */
export const paintTranscriptMediaContext = (
  mediaContext: Record<string, unknown> | null | undefined,
  ...transcriptCandidates: (string | null | undefined)[]
): Record<string, unknown> => {
  const base = { ...(mediaContext ?? {}) };
  const transcriptUrl = transcriptCandidates.find((candidate) =>
    isTranscriptJsonLocation(candidate),
  );
  if (!transcriptUrl) return base;
  const hadTranscriptUrl =
    isTranscriptJsonLocation(base.transcriptUrl as string | undefined) ||
    isTranscriptJsonLocation(base.transcript_url as string | undefined);
  return {
    ...base,
    transcriptUrl,
    ...(base.transcriptPosition === 'side' || base.transcriptPosition === 'below'
      ? { transcriptPosition: base.transcriptPosition }
      : {}),
    showTranscriptMarkers: hadTranscriptUrl ? (base.showTranscriptMarkers ?? true) : true,
    showTranscript: hadTranscriptUrl ? (base.showTranscript ?? true) : true,
  };
};

/**
 * Resolve the transcript artifact URL for a chat-history citation from ``sourceContext`` only.
 */
const resolveChatSourceTranscriptUrl = (source: Source): string | undefined =>
  resolveTranscriptUrlFromSourceContext(source.sourceContext as Record<string, unknown> | undefined);

/**
 * Single source of truth for "is this chat-history citation a transcript-bearing A/V source,
 * and where (if anywhere) is its transcript JSON?". Used by both {@link getUiSourceFromChatSource}
 * and {@link buildTimelineCopySourceContext} so the two cannot disagree on classification.
 *
 * The new pipeline's explicit ``has_transcript`` flag is authoritative when present; absent it,
 * we infer from URL presence. A citation can be classified ``hasTranscript: true`` without a resolvable URL if
 * the backend sends ``has_transcript: true`` but omits ``transcript_url`` — callers decide what
 * to do in that degenerate case (e.g. ``buildTimelineCopySourceContext`` suppresses the
 * transcript UI affordances since there'd be nothing to fetch).
 */
const classifyChatSourceTranscript = (
  source: Source,
): { hasTranscript: boolean; transcriptUrl: string | undefined } => {
  const ctx = source.sourceContext as Record<string, any> | undefined;
  const transcriptUrl = resolveChatSourceTranscriptUrl(source);
  const ctxHasTranscriptFlag =
    typeof ctx?.has_transcript === 'boolean' ? (ctx.has_transcript as boolean) : undefined;
  const hasTranscript =
    ctxHasTranscriptFlag !== undefined ? ctxHasTranscriptFlag : transcriptUrl !== undefined;
  return { hasTranscript, transcriptUrl };
};

/**
 * Build the ``sourceContext`` payload stashed in ``sessionStorage`` when copying a citation
 * into the timeline. Centralizes the transcript-vs-no-transcript branching so ``answer-item``
 * and ``question-bar`` cannot drift apart on which fields they set.
 *
 * Classification (``hasTranscript``) matches {@link getUiSourceFromChatSource} via
 * {@link classifyChatSourceTranscript}. The decision to paint timeline transcript UI controls
 * (``showTranscript`` / ``showTranscriptMarkers`` / ``transcriptPosition``) additionally
 * requires a resolvable URL — otherwise the timeline player would render "Hide Transcript"
 * controls with nothing to fetch.
 */
export const buildTimelineCopySourceContext = (
  source: Source,
): SourceContext => {
  const { hasTranscript, transcriptUrl } = classifyChatSourceTranscript(source);
  const paintTranscriptUi = hasTranscript && transcriptUrl !== undefined;
  return {
    ...source.sourceContext,
    ...(paintTranscriptUi
      ? {
          transcriptUrl: transcriptUrl,
          transcriptPosition: 'below' as const,
          showTranscriptMarkers: true,
          showTranscript: true,
        }
      : {
          transcriptUrl: undefined,
          showTranscriptMarkers: false,
          showTranscript: false,
        }),
    pageNumber: source.page || 1,
    startTime: source.sourceContext?.startTime || 0,
    previewUrl: source.previewFilePath || source.derived_object_url,
  };
};

/**
 * Resolves the 1-based citation page/slide for display directly from the
 * structured fields a chat source carries, independent of whether the legacy
 * stringy ``source.page`` has been back-filled yet.
 *
 * PowerPoint citations carry the slide via ``start_slide`` / ``slide_numbers``
 * (1:1 with the converted PDF page); paged documents carry
 * ``source_page_number``. We also accept the already-synced
 * ``sourceContext.pageNumber`` and finally the legacy stringy ``source.page``
 * so older history rows keep rendering. Returns ``undefined`` when no
 * page-like field is present (e.g. image-only citations) so callers can decide
 * whether to show a page/slide suffix at all.
 */
export const resolveCitationDisplayPage = (
  source: Pick<Source, 'page' | 'sourceContext'>,
): number | undefined => {
  const ctx = source.sourceContext as Record<string, any> | undefined;
  const rawCtxPage =
    ctx?.start_slide ??
    (Array.isArray(ctx?.slide_numbers) ? ctx?.slide_numbers[0] : undefined) ??
    ctx?.source_page_number ??
    ctx?.pageNumber;
  const ctxPageNumber =
    typeof rawCtxPage === 'number' && Number.isFinite(rawCtxPage) ? rawCtxPage : undefined;
  const legacyPageNumber =
    source.page && !isNaN(parseInt(source.page)) ? parseInt(source.page) : undefined;
  return ctxPageNumber ?? legacyPageNumber;
};

export const getUiSourceFromChatSource = (source: Source) => {
  const sourceType = extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown';
  const ctx = source.sourceContext as Record<string, any> | undefined;

  // chat-history's per-citation sourceContext carries structured A/V metadata
  // (``transcript_url``, ``start_time``, ``end_time``, ``has_transcript``, ...). Transcript URLs
  // are resolved only from ``sourceContext`` (not ``source.location``). Classification is shared
  // with the timeline-copy helper so a citation can never be A/V here and a paged document there.
  const { hasTranscript, transcriptUrl: ctxTranscriptUrl } = classifyChatSourceTranscript(source);
  const ctxStartTime =
    typeof ctx?.start_time === 'string' && ctx.start_time.length > 0
      ? (ctx.start_time as string)
      : undefined;
  const ctxEndTime =
    typeof ctx?.end_time === 'string' && ctx.end_time.length > 0
      ? (ctx.end_time as string)
      : undefined;

  const sourceObject: MediaSource = {
    ...source,
    sourceType: sourceType,
    sourceUrl: source.sourceFilePath,
    sourceContext: source.sourceContext || {},
  };
  if (!hasTranscript) {
    // chat-history's per-citation sourceContext now carries the structured
    // ``source_page_number`` (number for paged docs, null for image-only). PowerPoint
    // citations instead carry ``start_slide`` / ``end_slide`` / ``slide_numbers`` (a
    // 1-based slide list); the start slide maps 1:1 to the page of the converted PDF,
    // so prefer it when present. All fall back to the legacy stringy ``source.page``
    // when absent/non-numeric so older history rows that pre-date the schema keep
    // rendering. The same resolution drives the sources-list slide/page suffix
    // (see ``resolveCitationDisplayPage``), so keep them in sync via the shared helper.
    const pageNumber = resolveCitationDisplayPage(source);
    if (pageNumber !== undefined) {
      sourceObject.pageNumber = pageNumber;
      sourceObject.sourceContext!.pageNumber = pageNumber;
      // Keep the legacy stringy ``page`` field in sync so display call sites that still read
      // ``source.page`` directly (answer-item, sourceCard, copy-to-clipboard) reflect the
      // authoritative value rather than diverging from ``pageNumber``.
      (sourceObject as Source).page = String(pageNumber);
    }
  }
  if (hasTranscript) {
    sourceObject.sourceContext = {
      ...sourceObject.sourceContext,
      ...(ctxTranscriptUrl !== undefined ? { transcriptUrl: ctxTranscriptUrl } : {}),
    };
    if (ctxStartTime !== undefined) {
      sourceObject.sourceContext.startTime = ctxStartTime;
    } else if (source.content) {
      const firstStartTime = getFirstStartTime(source.content);
      if (firstStartTime) {
        sourceObject.sourceContext.startTime = firstStartTime;
      }
    }
    if (ctxEndTime !== undefined) {
      sourceObject.sourceContext.endTime = ctxEndTime;
    }
  }

  return sourceObject;
}

/**
 * True when a citation can be shown as JSON media from inline ``content`` alone
 * (no resolvable file URL). Matches MediaDisplay ``canShowJsonMedia`` for the
 * content-only path when JSON media flags are on.
 */
export function isInlineJsonCitationDisplayable(
  source: Source | MediaSource,
  preferredMediaType: string | undefined,
  jsonMediaDisplayEnabled: boolean,
): boolean {
  if (!jsonMediaDisplayEnabled) return false;
  const content = typeof source.content === 'string' ? source.content.trim() : '';
  if (!content) return false;
  if (preferredMediaType === 'json') return true;

  const pathHints = [
    (source as Source).previewFilePath,
    getDispatchedFilePath(source as Source),
    source.sourceFilePath,
    source.sourceUrl,
  ];
  for (const path of pathHints) {
    const trimmed = typeof path === 'string' ? path.trim() : '';
    if (!trimmed) continue;
    if (
      getMediaType(
        { mediaType: 'unknown', mediaUrl: trimmed } as MediaSource,
        source.fileInfo ?? null,
      ) === 'json'
    ) {
      return true;
    }
  }

  if (
    (content.startsWith('{') && content.endsWith('}')) ||
    (content.startsWith('[') && content.endsWith(']'))
  ) {
    try {
      JSON.parse(content);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

export const getBestMediaFromSources = (
  sources: Source[],
  jsonMediaDisplayEnabled = false,
): MediaSource[] => {
  try {
    type PreferredCandidate = {
      source: Source;
      preferred: ReturnType<typeof resolvePreferredMediaDisplayForSource>;
      /** Citation has inline JSON ``content`` but no resolvable display URL. */
      contentOnlyJson?: boolean;
    };
    const candidates: PreferredCandidate[] = [];
    for (const source of sources) {
      const preferred = resolvePreferredMediaDisplayForSource(source, source.fileInfo);
      if (
        preferred.mediaUrl &&
        !isArchiveMemberSearchablePath(preferred.mediaUrl) &&
        isDisplayableMediaType(preferred.mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled })
      ) {
        candidates.push({ source, preferred });
        continue;
      }
      // processCitation / chat media may already carry a displayable URL while
      // preferred still points at a virtual archive-member path.
      const asMedia = source as MediaSource;
      const existingUrl = (asMedia.mediaUrl || asMedia.mediaSrc || '').trim();
      if (existingUrl && !isArchiveMemberSearchablePath(existingUrl)) {
        const existingType = getMediaType(
          {
            mediaType: asMedia.mediaType || 'unknown',
            mediaUrl: existingUrl,
          } as MediaSource,
          source.fileInfo,
        );
        const resolvedType =
          existingType !== 'unknown' ? existingType : asMedia.mediaType || preferred.mediaType;
        if (
          isDisplayableMediaType(resolvedType, { jsonMediaDisplay: jsonMediaDisplayEnabled })
        ) {
          candidates.push({
            source,
            preferred: {
              mediaUrl: existingUrl,
              mediaType: resolvedType as MediaType,
              selectedBy: 'none',
            },
          });
          continue;
        }
      }
      if (isInlineJsonCitationDisplayable(source, preferred.mediaType, jsonMediaDisplayEnabled)) {
        candidates.push({
          source,
          preferred: {
            mediaUrl: undefined,
            mediaType: 'json',
            selectedBy: 'none',
          },
          contentOnlyJson: true,
        });
      }
    }

    const typePriority: MediaType[] = ['image', 'video', 'audio', 'pdf'];
    let best =
      typePriority
        .map((type) => candidates.find((c) => !c.contentOnlyJson && c.preferred.mediaType === type))
        .find(Boolean) ??
      candidates.find((c) => !c.contentOnlyJson) ??
      candidates[0];
    if (!best) {
      return [] as MediaSource[];
    }

    const { source, preferred, contentOnlyJson } = best;
    const pageNumber = parseCitationPageFromSourceLike(source);
    const canonicalSourceUrl = source.sourceFilePath || source.sourceUrl;
    const mediaUrl = preferred.mediaUrl || '';
    const media: MediaSource = {
      ...(source as MediaSource),
      mediaType: contentOnlyJson ? 'json' : preferred.mediaType,
      mediaUrl: mediaUrl || undefined,
      mediaSrc: mediaUrl || undefined,
      sourceFilePath: source.sourceFilePath,
      sourceUrl: canonicalSourceUrl,
      originalUrl: canonicalSourceUrl,
      fileInfo: source.fileInfo,
      content: source.content,
      ...(preferred.selectedBy === 'previewFilePath'
        ? { previewFilePath: preferred.mediaUrl }
        : preferred.selectedBy === 'dispatchedFilePath'
          ? { dispatchedFilePath: preferred.mediaUrl }
          : {}),
      mediaContext: {
        ...((source as MediaSource).sourceContext || {}),
        pageNumber,
        ...(mediaUrl && mediaUrl !== canonicalSourceUrl ? { previewUrl: mediaUrl } : {}),
      },
    };
    return [media];
  } catch (error) {
    return [] as MediaSource[];
  }
};

export const parseEvents = (event: EventFromChat) => {
  const monthPrefixes = ['late', 'early', 'mid'];
  const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const shortMonths = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const lines = event.answerText.split('\n');
  const parsedEvents: Slide[] = [];
  let currentYear = undefined;
  let lastYear = undefined;
  let currentMonth = undefined;
  let lastMonth = undefined;
  let eventMonth = undefined;
  let eventDay = undefined;
  let eventText = undefined;
  let description = '';
  let matchingSources: Source[] = [];
  for (const line of lines) {
    let eventTime = '12:00:00';
    let timeIsValid = false;
    if (line.startsWith('#') || line.startsWith('##') || line.startsWith('###')) {
      const monthAndYearRegex = new RegExp(`(${monthPrefixes.join('|')}|${months.join('|')}|${shortMonths.join('|')})\\s+(\\d{4})`, 'i');
      const monthAndYearMatches = line.match(monthAndYearRegex);
      if (monthAndYearMatches && monthAndYearMatches.length > 0) {
        const month = monthAndYearMatches[1];
        const year = monthAndYearMatches[2];
        currentMonth = monthPrefixes.includes(month.toLowerCase()) ? undefined : month;
        currentYear = year;
        eventMonth = undefined;
        eventDay = undefined;
        eventText = undefined;
      } else {
        currentMonth = undefined;
        currentYear = undefined;
      }
    } else if (line.startsWith('- ')) {
      let extractedDate = undefined;
      const extractedDates = extractDate(line, {
        direction: 'MD',
        locale: 'en',
        timeZone: 'America/New_York',
      });
      if (extractedDates && extractedDates.length > 0) {
        let extractedDate0 = extractedDates[0].date;
        const extractedDateParts = extractedDate0.split('-');
        extractedDate = `${currentYear}-${extractedDateParts[1]}-${extractedDateParts[2]}`;
        const month = extractedDateParts[1];
        const day = extractedDateParts[2];
        let item = line.replace(/<sup>.*?<\/sup>/g, '').replace(/<\/p><p>/g, '').trim().slice(2);
        eventTime = '12:00:00';
        timeIsValid = false;
        const extractedTimes = extractTime(item.replaceAll('(', ' ').replaceAll(')', ' '), 24);
        if (extractedTimes && extractedTimes.length > 0) {
          eventTime = extractedTimes[0].time;
          const timeParts = eventTime.split(':');
          const hours = timeParts[0].length === 1 ? `0${timeParts[0]}` : timeParts[0];
          const minutes = timeParts[1].length === 1 ? `0${timeParts[1]}` : timeParts[1];
          const seconds = timeParts[2] ? timeParts[2].length === 1 ? `0${timeParts[2]}` : timeParts[2] : '00';
          eventTime = `${hours}:${minutes}:${seconds}`;
          timeIsValid = true;
        }
        eventMonth = month;
        eventDay = day;
        eventText = item;
        description = '';
      } else {
        eventMonth = undefined;
        eventDay = undefined;
        eventText = undefined;
        description = '';
      }
      if (extractedDate && event.sources) {
        matchingSources = getMatchingSources(event.sources, extractedDate);
      }
    } else if (line.startsWith('  - ')) {
      description = line.replace(/<sup>.*?<\/sup>/g, '').replace(/<\/p><p>/g, '');
    }

    if (!currentMonth || !currentYear || lastMonth !== currentMonth || lastYear !== currentYear) {
      lastMonth = currentMonth;
      lastYear = currentYear;
      eventMonth = undefined;
      eventDay = undefined;
      eventText = undefined;
      description = '';
    } else if (currentMonth && currentYear && eventDay && eventText) {
      try {
        const eventDate = new Date(`${currentMonth} ${eventDay}, ${currentYear}, 12:00:00`).toISOString().replace(/T.*Z/, `T${eventTime}.000Z`);
        let sources = event.sources.slice(0, 12) as MediaSource[];
        if (matchingSources.length > 0) {
          sources = [...matchingSources].concat(event.sources.slice(0, 12)) ? event.sources.slice(0, 12) as MediaSource[] : [];
        }
        const media = getBestMediaFromSources(sources);
        const newEvent = {
          date: eventDate,
          slideTitle: eventText,
          slideDescription: '',
          isTimeValid: timeIsValid,
          show: true,
          sources: sources as MediaSource[],
          media: media,
        }
        if (!deepEqual(newEvent, parsedEvents[parsedEvents.length - 1])) {
          parsedEvents.push(newEvent);
        }
      } catch (error) {
        // do nothing
      }
    }
    if (description && parsedEvents.length > 0 && parsedEvents[parsedEvents.length - 1] && parsedEvents[parsedEvents.length - 1].slideDescription) {
      parsedEvents[parsedEvents.length - 1].slideDescription += description;
    }
  }

  return parsedEvents;
}

export const buildScopeFilter = (scopes: string[], files: string[], excludedFiles: string[], selectedCase: string, caseField: string) => {
  try {
    const caseFilter = selectedCase === "*" ? undefined : {
      equals: {
        key: caseField,
        value: selectedCase,
      },
    };

    const hasScopes = scopes.length > 0;
    const hasFiles = files.length > 0;
    // if there are included files, then excluded files are not used
    const hasExcludedFiles = !hasFiles && excludedFiles.length > 0;
    let orAllFilters: any[] = [];
    let excludedFilesFilter = null;

    // these are or-able filters if > 1
    if (hasScopes || hasFiles) {
      if (hasScopes) {
        scopes.forEach(scope => {
          const hash = SparkMD5.hash(scope);
          orAllFilters.push({
            listContains: {
              key: 'scope',
              value: hash,
            },
          });
        });
      }
      if (hasFiles) {
        const fileHashes = files.map(file => {
          const hash = SparkMD5.hash(file);
          return hash;
        });
        orAllFilters.push({
          in: {
            key: 'source_url_hash',
            value: fileHashes,
          },
        });
      }
    }

    if (hasExcludedFiles) {
      const fileHashes = excludedFiles.map(file => {
        const hash = SparkMD5.hash(file);
        return hash;
      });
      excludedFilesFilter = {
        notIn: {
          key: 'source_url_hash',
          value: fileHashes,
        },
      };
    }

    const useOrAll = orAllFilters.length > 1;
    const useAndAll = (caseFilter && (hasScopes || hasFiles || hasExcludedFiles))
      || ((hasScopes || hasFiles) && hasExcludedFiles)
      || excludedFilesFilter;

    let filter = undefined;

    try {
      if (useAndAll) {
        filter = { andAll: [] };
      } else if (useOrAll) {
        filter = { orAll: [] };
      } else if (caseFilter) {
        filter = caseFilter;
      }

      if (useAndAll) {
        if (caseFilter) {
          (filter as { andAll: any[] }).andAll.push(caseFilter);
        }
        if (useOrAll) {
          (filter as { andAll: any[] }).andAll.push({ orAll: orAllFilters });
        } else if (orAllFilters.length === 1) {
          (filter as { andAll: any[] }).andAll.push(orAllFilters[0]);
        }
        if (hasExcludedFiles) {
          (filter as { andAll: any[] }).andAll.push(excludedFilesFilter);
        }
      } else if (useOrAll) {
        (filter as { orAll: any[] }).orAll = orAllFilters;
      } else if (orAllFilters.length === 1) {
        filter = orAllFilters[0];
      } else if (hasExcludedFiles) {
        filter = {
          andAll: [excludedFilesFilter],
        };
      } else if (caseFilter) {
        filter = caseFilter;
      }
    } catch (error) {
      console.log('Error building SCOPE filter 1: ', error);
      filter = undefined;
    }
    return filter;
  } catch (error) {
    console.log('Error building SCOPE filter 2: ', error);
    return '';
  }
}

export const getTimezoneList = () => {
  /*
  const allTimezones = Intl.supportedValuesOf('timeZone').map(timeZone => {
    const dtfs = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'shortOffset' });
    const offset = dtfs ? dtfs.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : null;
    const dtf = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'long' });
    const timeZoneAbbreviation = dtf ? dtf.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : null;
    const offsetInMilliseconds = offset ? parseInt(offset.replace(/^GMT/, '')) * 3600000 : undefined;
    return {
      name: timeZone,
      abbreviation: timeZoneAbbreviation,
      offset: offset,
      offsetInMilliseconds: offsetInMilliseconds,
    }
  });
  */
  return US_TIMEZONES_PLUS_UTC;
}

export const getTimezoneListByName = (): Record<string, TimezoneDetails> => {
  const timezoneListByName: Record<string, TimezoneDetails> = {};
  Intl.supportedValuesOf('timeZone').forEach(timeZone => {
    const dtfs = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'shortOffset' });
    const offset = dtfs ? dtfs.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : undefined;
    const dtf = new Intl.DateTimeFormat('en', { timeZone: timeZone, timeZoneName: 'long' });
    const timeZoneAbbrivation = dtf ? dtf.formatToParts().find((part: any) => part.type === 'timeZoneName')?.value : undefined;
    timezoneListByName[timeZone] = {
      name: timeZone,
      abbreviation: timeZoneAbbrivation,
      offset: offset,
      offsetInMilliseconds: offset ? parseInt(offset.replace(/^GMT/, '')) * 3600000 : undefined,
    }
  });
  return timezoneListByName;
}

export const filterSources = (sources: MediaSource[]): MediaSource[] => {
  return sources.filter((source) => {
    const filename = (source.truncatedFilePath || source.sourceUrl || '').split('/').pop() || 'Unknown';
    return source.sourceUrl && source.sourceUrl.trim() !== "" && filename !== "Unknown";
  });
}

export const filterMedia = (media: MediaSource[]): MediaSource[] => {
  return media.filter((mediaItem) => {
    const mediaUrl = (mediaItem.mediaUrl || mediaItem.mediaSrc || '').trim();
    if (mediaUrl) {
      const filename = mediaUrl.split('/').pop() || 'Unknown';
      return filename !== 'Unknown';
    }
    // Content-only JSON citations have no mediaUrl/mediaSrc — keep when the
    // excerpt is present so they survive slide rebuild / timeline media aggregates.
    return (
      mediaItem.mediaType === 'json' &&
      typeof mediaItem.content === 'string' &&
      mediaItem.content.trim() !== ''
    );
  });
}

/** Build a display-ready MediaSource from a timeline title-slide media URL (s3 or presigned https). */
export const buildMediaSourceFromUrl = (url: string): MediaSource | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const mediaObject: MediaSource = {
    mediaSrc: trimmed,
    mediaUrl: trimmed,
    originalUrl: trimmed,
    mediaType: 'unknown' as MediaType,
  };
  mediaObject.mediaType = getMediaType(mediaObject, mediaObject.fileInfo);
  return mediaObject;
}

const pickFirstS3Url = (...candidates: (string | undefined | null)[]): string | undefined => {
  for (const candidate of candidates) {
    const value = (candidate ?? '').trim();
    if (value.startsWith('s3://')) return value;
  }
  return undefined;
};

const resolveS3UrlFromMediaLike = (
  obj: MediaSource | null | undefined,
): string | undefined => {
  if (!obj) return undefined;
  return pickFirstS3Url(
    obj.s3Url,
    obj.fileInfo?.fileUrl,
    obj.originalUrl,
    obj.sourceUrl,
    obj.sourceFilePath,
    obj.mediaSrc,
    obj.mediaUrl,
  );
};

/**
 * Best full ``s3://<bucket>/<key>`` URL for a source/media object, scanning the
 * canonical pointer fields (``s3Url`` → ``fileInfo.fileUrl`` → ``originalUrl`` →
 * ``sourceUrl`` → ``sourceFilePath`` → ``mediaSrc`` → ``mediaUrl``). Use this to
 * feed provenance lookups (e.g. ``getCurrentFileInfo``) a full URL, since a bare
 * relative key can't be upgraded client-side without the bucket. Returns
 * ``fallback`` (default ``''``) when none of the fields holds a full ``s3://`` URL.
 */
export const resolveFullS3Url = (
  source: (Partial<MediaSource> & Partial<Source>) | null | undefined,
  fallback = '',
): string => {
  if (!source) return fallback;
  const candidate = pickFirstS3Url(
    (source as MediaSource).s3Url,
    source.fileInfo?.fileUrl,
    source.originalUrl,
    source.sourceUrl,
    source.sourceFilePath,
    (source as MediaSource).mediaSrc,
    (source as MediaSource).mediaUrl,
  );
  return candidate ?? fallback;
};

/** True when ``url`` is provenance's transcode ``previewUrl``, not a separate file-info lookup key. */
export function isProvenanceDerivedPreviewUrl(
  url: string | undefined | null,
  fileInfo: FileSummary | null | undefined,
): boolean {
  const candidate = typeof url === 'string' ? url.trim() : '';
  const preview =
    typeof fileInfo?.previewUrl === 'string' ? fileInfo.previewUrl.trim() : '';
  return !!candidate && !!preview && candidate === preview;
}

/**
 * True when a FileSummary describes a container itself rather than an extracted
 * subdocument. This matters when a citation row retains the parent archive's
 * summary but carries the cited extract in its fallback ``s3Url``.
 */
function isContainerFileSummary(fileInfo: FileSummary | null | undefined): boolean {
  if (!fileInfo) return false;
  const extension = (fileInfo.extension ?? '').toLowerCase().replace(/^\./, '');
  if (CONTAINER_SELF_EXTENSIONS.includes(extension) || extension === 'mbox' || extension === 'mbx') {
    return true;
  }
  if (CONTAINER_SELF_MIME_TYPES.has((fileInfo.mimeType ?? '').toLowerCase())) return true;
  return (fileInfo.contentCategories ?? []).some((category) =>
    isContainerLineageCategory(String(category)),
  );
}

/** Playback URL from a provenance ``FileSummary``: ``previewUrl``, else ``fileUrl``. */
export function resolveFileInfoMediaDisplayUrl(
  fileInfo: FileSummary | null | undefined,
  fallbackUrl: string,
): string {
  const fallback = fallbackUrl.trim();
  // A processed extract URL carried on the source is authoritative over a
  // parent archive summary. Otherwise a citation inside ``archive.zip`` can
  // incorrectly preview the zip itself and skip tree/extract resolution.
  if (
    fallback &&
    !isArchiveMemberSearchablePath(fallback) &&
    isContainerFileSummary(fileInfo)
  ) {
    return fallback;
  }
  const candidates = [
    typeof fileInfo?.previewUrl === 'string' ? fileInfo.previewUrl.trim() : '',
    typeof fileInfo?.fileUrl === 'string' ? fileInfo.fileUrl.trim() : '',
    typeof fileInfo?.downloadUrl === 'string' ? fileInfo.downloadUrl.trim() : '',
    fallback,
  ];
  for (const url of candidates) {
    if (url && !isArchiveMemberSearchablePath(url)) return url;
  }
  return '';
}

/**
 * Chat-history citations carry chunk text, location, page/time context, or a
 * truncated path. Evidence-locker / timeline rows typically do not.
 */
export function isChatCitationSource(
  source: Source | MediaSource | null | undefined,
): boolean {
  if (!source) return false;
  const s = source as Source;
  const pageRaw = s.page ?? s.pageNumber;
  const hasPage =
    pageRaw !== undefined && pageRaw !== null && String(pageRaw).trim() !== '';
  return !!(
    s.content ||
    s.location ||
    s.truncatedFilePath ||
    s.sourceContext?.startTime != null ||
    s.sourceContext?.endTime != null ||
    s.sourceContext?.transcriptUrl ||
    hasPage
  );
}

/**
 * Whether provenance ``/files/file-info`` must be fetched before mounting
 * ``MediaDisplay``.
 *
 * - Non-citations (evidence locker, share tray, …): fetch once when missing.
 * - Chat citations: when citation ``previewFilePath`` / ``dispatchedFilePath`` or
 *   preloaded ``fileInfo`` are not already on the source (needed to discover
 *   provenance ``previewUrl``).
 */
export function shouldFetchFileInfoBeforeDisplay(
  source: Source | MediaSource | null | undefined,
): boolean {
  if (!source) return false;

  const lookupPath = (
    source.sourceFilePath ||
    source.sourceUrl ||
    (source as MediaSource).mediaUrl ||
    (source as MediaSource).mediaSrc ||
    ''
  ).trim();
  if (!lookupPath) return false;

  // Chat citations never arrive with authoritative file-info (any ``fileInfo``
  // attached upstream can point at the parent container, not the cited
  // extract), so always load it fresh on click. The lookup targets the
  // dispatched object for containers — see ``resolveDisplayLookupUrl``.
  if (isChatCitationSource(source)) return true;

  // Original / ``bypass_cm`` must re-fetch: locker ``fileInfo`` is the CM-safe
  // transcode, not the original asset.
  if (source.bypassCm) return true;

  // Evidence-locker / share-tray rows are provenance-fed and carry correct
  // file-info; only fetch when it is missing.
  return !source.fileInfo;
}

/**
 * Returns the ``s3://`` URL that should be used to look up provenance
 * ``fileInfo`` for the **object actually being displayed**.
 *
 * For chat citations pointing at an extract inside a container (e.g. a
 * UFDR/Cellebrite video at ``…/Video/PART_…`` inside a ``.ufdr`` report),
 * the citation source carries the **parent container** in
 * ``sourceFilePath``/``sourceUrl`` and the extract in
 * ``previewFilePath``/``dispatchedFilePath``. The default
 * {@link resolveFullS3Url} precedence would pick the parent and provenance
 * would correctly answer for the parent — wrong question for "what is the
 * media at the cited piece?".
 *
 * The ``dispatchedFilePath`` is the actual extracted object the citation points
 * at and is always the correct target for the provenance file-info lookup: the
 * ``previewFilePath`` / ``previewUrl`` is only a renderable proxy (a thumbnail
 * or transcode) and must drive *display*, not the metadata fetch. Image
 * extracts happened to resolve correctly already because their dispatched path
 * is self-typing by extension; audio/video extracts frequently carry no
 * extension (e.g. UFDR/Cellebrite ``…/Video/PART_…``), so without preferring
 * the dispatched url unconditionally the preview thumbnail would win and we'd
 * fetch file-info for the wrong object (TP-3247).
 *
 * After the dispatched url, this helper prefers the citation extract when
 * {@link resolvePreferredMediaDisplayForSource} resolves via a citation
 * branch and the chosen URL is already fully qualified; otherwise it
 * falls back to {@link resolveFullS3Url}.
 */
export const resolveDisplayLookupUrl = (
  source: (Partial<MediaSource> & Partial<Source>) | null | undefined,
  fileInfo?: FileSummary | null,
  fallback = '',
): string => {
  if (!source) return fallback;
  // A citation's dispatched object url is always the right file-info lookup
  // target, regardless of media type. Display precedence (preview vs dispatched)
  // is handled separately by ``resolvePreferredMediaDisplayForSource``.
  const dispatchedFilePath = getDispatchedFilePath(source);
  if (dispatchedFilePath && dispatchedFilePath.startsWith('s3://')) {
    return dispatchedFilePath;
  }
  const effectiveFileInfo = fileInfo ?? source.fileInfo;
  const preferred = resolvePreferredMediaDisplayForSource(
    source as Source | MediaSource,
    effectiveFileInfo,
  );
  // ``fileInfo.previewUrl`` is for playback — file-info was already fetched for
  // the source URL; do not re-query provenance using the transcode URL.
  if (
    preferred.selectedBy === 'derivedPreview' &&
    isProvenanceDerivedPreviewUrl(preferred.mediaUrl, effectiveFileInfo)
  ) {
    return resolveFullS3Url(source, fallback);
  }
  if (
    (preferred.selectedBy === 'previewFilePath' || preferred.selectedBy === 'dispatchedFilePath') &&
    typeof preferred.mediaUrl === 'string' &&
    preferred.mediaUrl.startsWith('s3://')
  ) {
    if (isProvenanceDerivedPreviewUrl(preferred.mediaUrl, effectiveFileInfo)) {
      return resolveFullS3Url(source, fallback);
    }
    return preferred.mediaUrl;
  }
  return resolveFullS3Url(source, fallback);
};

/**
 * Canonical URL for timeline.media on PATCH — full ``s3://`` URI when available
 * (same precedence as ``mapSlideToTimelineEventPayload``: ``s3Url`` / ``fileInfo.fileUrl``).
 */
export const getTimelineMediaPersistUrl = (
  media: MediaSource | string | undefined | null,
  source?: MediaSource | null,
): string | undefined => {
  if (typeof media === 'string') {
    const trimmed = media.trim();
    if (!trimmed) return undefined;
    if (isLikelyBase64MediaValue(trimmed)) return trimmed;
    return pickFirstS3Url(trimmed) ?? resolveS3UrlFromMediaLike(source) ?? trimmed;
  }

  if (!media && !source) return undefined;

  return (
    resolveS3UrlFromMediaLike(media) ??
    resolveS3UrlFromMediaLike(source) ??
    [media?.s3Url, media?.mediaSrc, media?.mediaUrl, source?.sourceUrl]
      .map((value) => value?.trim())
      .find(Boolean)
  );
}

/** True when timeline.media appears to hold a raw base64 payload (not a file path or URL). */
export function isLikelyBase64MediaValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('data:')) return true;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('s3://')) return false;
  // File paths use slashes; embedded base64 blobs typically do not.
  if (trimmed.includes('/')) return false;
  if (trimmed.length < 32) return false;
  return /^[A-Za-z0-9+/=\r\n]+$/.test(trimmed);
}

/** True when timeline.media looks like a storage path/key but is not yet a full ``s3://`` URI. */
export function isLikelyFilePathMediaValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('s3://')) return false;
  if (isLikelyBase64MediaValue(trimmed)) return false;
  return trimmed.includes('/');
}

/**
 * Resolve timeline.media for PATCH, upgrading bare paths to full ``s3://`` URIs via
 * provenance ``file-info`` when needed. Leaves base64 payloads unchanged.
 */
export async function resolveTimelineMediaPersistUrl(
  media: MediaSource | string | undefined | null,
  source: MediaSource | null | undefined,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<string | undefined> {
  const syncResolved = getTimelineMediaPersistUrl(media, source);
  if (syncResolved?.startsWith('s3://')) return syncResolved;

  const pathCandidates = [
    typeof media === 'string' ? media.trim() : undefined,
    syncResolved,
    typeof media === 'object' && media ? media.mediaSrc : undefined,
    typeof media === 'object' && media ? media.mediaUrl : undefined,
    source?.sourceUrl,
    source?.sourceFilePath,
  ];

  for (const candidate of pathCandidates) {
    const trimmed = candidate?.trim();
    if (!trimmed || !isLikelyFilePathMediaValue(trimmed)) continue;
    const fileInfo = await getFileInfoFromServer(trimmed, cellId, accessToken);
    if (fileInfo?.fileUrl?.startsWith('s3://')) {
      return fileInfo.fileUrl;
    }
  }

  if (typeof media === 'string' && isLikelyBase64MediaValue(media.trim())) {
    return media.trim();
  }

  return syncResolved;
}

export const getDateFromDateStr = (dateStr: string | null): CalendarDate | null => {
  if (!dateStr) {
    return null;
  }
  const dateParts = dateStr.split('-');
  const year = parseInt(dateParts[0]);
  const month = parseInt(dateParts[1]);
  const day = parseInt(dateParts[2]);
  return new CalendarDate(year, month, day);
};

// note 0, 0 is MIDNIGHT and AM; and 12, 0 is NOON and PM
export const getTimeFromStr = (timeStr: string | null): TimeParts => {
  const timeParts = timeStr?.split(':');
  let hours = timeParts?.[0];
  const minutes = timeParts?.[1];
  const secondsAndPeriod = timeParts?.[2];
  const seconds = secondsAndPeriod?.split(' ')[0];
  const period = secondsAndPeriod?.split(' ')[1];

  const addedHours = period?.toLowerCase() === 'pm' ? 12 : 0;
  if (period?.toLowerCase() === 'am') {
    if (hours === '12') {
      hours = '0';
    }
  }
  let hoursNumber = hours ? parseInt(hours) : undefined;

  if (hoursNumber !== undefined && hoursNumber < 12) {
    hoursNumber = hoursNumber + addedHours;
  }
  const minutesNumber = minutes ? parseInt(minutes) : undefined;
  const secondsNumber = seconds ? parseInt(seconds) : undefined;
  if (hours === undefined || minutes === undefined || seconds === undefined) {
    return { hoursNumber: undefined, minutesNumber: undefined, secondsNumber: undefined };
  }
  return { hoursNumber, minutesNumber, secondsNumber };
};

export const getClientTimeFromStr = (timeStr: string | null): Time | null => {
  const { hoursNumber, minutesNumber, secondsNumber } = getTimeFromStr(timeStr);
  if (hoursNumber !== undefined && minutesNumber !== undefined && secondsNumber !== undefined) {
    return new Time (hoursNumber, minutesNumber, secondsNumber);
  }
  return null;
}

// Usage Tracking (Intercom telemetry; also used by recordStat)
export const trackEvent = async (eventName: string, eventData: any) => {
  try {
    let errorMessage = undefined;
    let warningMessage = undefined;
    const intercomEventData: any = {
      user_id: eventData.user,
      case_id: eventData.case_id,
      session_id: eventData.session_id,
      model_arn: eventData.model_arn,
    };
    if (eventData.target_id != null && eventData.target_id !== '') {
      intercomEventData.target_id = eventData.target_id;
    }
    if (eventName === 'error' && eventData.message && eventData.message.trim() !== '') {
      errorMessage = eventData.message;
      intercomEventData.error_message = errorMessage;
    } else if (eventName === 'warning' && eventData.message && eventData.message.trim() !== '') {
      warningMessage = eventData.message;
      intercomEventData.warning_message = warningMessage;
    }
    trackIntercomEvent(eventName, intercomEventData);
    const inhouseEventData: any = {
      user: eventData.user,
      case_id: eventData.case_id,
      session_id: eventData.session_id,
      model_arn: eventData.model_arn || undefined,
      reason: errorMessage || warningMessage || eventData.reason || undefined,
      question: eventData.question || undefined,
      answer: eventData.answer || undefined,
    };
    if (eventData.target_id != null && eventData.target_id !== '') {
      inhouseEventData.target_id = eventData.target_id;
    }
    // Forward the chat-history archive turn key when present (set by chat
    // feedback events) so stored feedback rows can point at the exact turn
    // (chat_key + epoch_time_secs = archive PK/SK). Only added when provided,
    // so non-chat telemetry events are unaffected.
    if (eventData.chat_key != null && eventData.chat_key !== '') {
      inhouseEventData.chat_key = eventData.chat_key;
    }
    if (eventData.citation != null) {
      inhouseEventData.citation = eventData.citation;
    }
    if (eventData.epoch_time_secs != null) {
      inhouseEventData.epoch_time_secs = eventData.epoch_time_secs;
    }
    if (eventData.request_type != null && eventData.request_type !== '') {
      inhouseEventData.request_type = eventData.request_type;
    }
    if (eventData.identity != null && typeof eventData.identity === 'object') {
      inhouseEventData.identity = eventData.identity;
    }
    if (eventData.work_product_name != null && eventData.work_product_name !== '') {
      inhouseEventData.work_product_name = eventData.work_product_name;
    }
    if (eventData.success != null) {
      inhouseEventData.success = eventData.success;
    }
    const session = await getGatewaySession();
    if (session) {
      await postTelemetryEvent({
        cellId: session.cellId,
        accessToken: session.accessToken,
        body: { event_name: eventName, event_data: inhouseEventData as Record<string, unknown> },
      });
    }
  } catch {
    // do nothing
  }
};

// If tracking does not find an id, you can use a className like id-<some name> to try to id it
export function recordStat(
  eventName: string | undefined,
  event: React.MouseEvent | PressEvent | undefined,
  statsContext: { userInfo: { userId: string; sessionId: string; caseId: string; query: string } },
) {
  let elementId: string | undefined = undefined;
  let eventNameToUse = eventName && eventName.trim() !== '' ? eventName : undefined;
  const target = event?.target as HTMLElement;
  if (!eventNameToUse && target) {
    const dataTestId = (target as HTMLElement & { 'data-testid'?: string })['data-testid'];
    elementId = dataTestId || target.id;
    if (!elementId) {
      const className = target.className;
      if (className && typeof className === 'string') {
        const id = className.split(' ').find(c => c.startsWith('id-'));
        if (id) {
          elementId = id.replace('id-', '');
        }
      }
      if (!elementId) {
        const parentWithDataTestId = target.closest('[data-testid]');
        const parentWithId = target.closest('[id]');
        if (parentWithDataTestId) {
          elementId = parentWithDataTestId.getAttribute('data-testid') || 'unknown';
        } else if (parentWithId) {
          elementId = parentWithId.id;
        }
      }
    }
  }
  if (!eventNameToUse) {
    eventNameToUse = elementId || 'unknown';
  }
  trackEvent(eventNameToUse || 'unknown', {
    user: statsContext?.userInfo?.userId || 'unknown',
    case_id: statsContext?.userInfo?.caseId || 'unknown',
    session_id: statsContext?.userInfo?.sessionId || 'unknown',
    target_id: elementId || 'unknown',
  });
  console.log('Event data captured');
}

/**
 * Client-only error reporting (components, hooks, browser fetch helpers). API routes and
 * `use server` code should use {@link CaptureErrorServer} from `@/shared/serverFunctions`.
 */
export function captureError(
  errorMessage: string,
  error: Error | undefined = undefined,
) {
  console.error(`${errorMessage}: ${error ? error.message : ''}`);
  void trackEvent('error', { message: errorMessage });
}

/**
 * Client-only warning reporting. For routes, use {@link CaptureWarningServer} in serverFunctions.
 */
export function captureWarning(
  warningMessage: string,
  error: Error | undefined = undefined,
) {
  console.warn(`${warningMessage}: ${error ? error.message : ''}`);
  void trackEvent('warning', { message: warningMessage });
}

/** @deprecated Prefer {@link captureError} — kept for existing call sites. */
export const CaptureError = captureError;
/** @deprecated Prefer {@link captureWarning} */
export const CaptureWarning = captureWarning;

export function parseS3Uri(uri: string): { bucket: string; key: string } {
  const s3Prefix = 's3://';
  if (!uri.startsWith(s3Prefix)) {
    throw new Error('Invalid S3 URI: Must start with "s3://"');
  }

  const path = uri.slice(s3Prefix.length);
  const [bucket, ...keyParts] = path.split('/');

  if (!bucket) {
    throw new Error('Invalid S3 URI: No bucket specified');
  }

  const key = keyParts.join('/');

  return { bucket, key };
}

export function IsARefusal(
  response: string,
  refusalMessages: string[] = REFUSAL_MESSAGES,
  passMessages: string[] = PASS_MESSAGES,
  citations: any[] = [] // array of citations or sources
): boolean {
  if (citations.length > 0) {
    return false;
  }
  const lowerCaseResponse = response.toLowerCase();
  const passMatch = passMessages.some(message => lowerCaseResponse.includes(message.toLowerCase()));
  if (passMatch) {
    return false;
  }
  const refusalMatch = refusalMessages.some(message => lowerCaseResponse.includes(message.toLowerCase()));
  return refusalMatch;
}

export function IsNotARefusal(
  response: string,
  refusalMessages: string[] = REFUSAL_MESSAGES,
  passMessages: string[] = PASS_MESSAGES,
  citations: any[] = [] // array of citations or sources
): boolean {
  return !IsARefusal(response, refusalMessages, passMessages, citations);
}

export function formatTime(timeStr: string): string {
  try {
    if (!timeStr) return '';
    timeStr = timeStr.toLowerCase().trim();

    // Convert 24-hour format to 12-hour format without seconds
    const convertTo12Hour = (hours: number, minutes: number): string => {
      const period = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12;
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    // Handle 24-hour format (e.g., "14:30" or "23:45")
    if (timeStr.match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return convertTo12Hour(hours, minutes);
    }

    // Handle 12-hour format (e.g., "2:30 pm")
    if (timeStr.match(/^\d{1,2}:\d{2}\s*[ap]m$/i)) {
      const [time, period] = timeStr.split(/\s+/);
      const [hour, minutes] = time.split(':').map(Number);
      const hours = period.toLowerCase() === 'pm' && hour !== 12 ? hour + 12 : hour;
      return convertTo12Hour(hours, minutes);
    }

    // Handle single hour in 24-hour format (e.g., "14" or "23")
    if (timeStr.match(/^([01]?[0-9]|2[0-3])$/)) {
      const hours = parseInt(timeStr);
      return convertTo12Hour(hours, 0);
    }

    return timeStr;
  } catch (error) {
    console.error('Error formatting time:', timeStr);
    return timeStr;
  }
};

export function GetAWSRegion(): string {
  const region = process.env.AWS_REGION || process.env.AWS_DEFAULT_REGION || 'us-east-1';
  if (!process.env.AWS_REGION && !process.env.AWS_DEFAULT_REGION) {
    const errorMessage = "AWS region is missing from environment variables, using default of us-east-1";
    console.error(errorMessage);
  }
  return region;
}

// SessionStorage key and max count for token-expired reload guard (prevents infinite loop)
const TOKEN_EXPIRED_RELOAD_KEY = 'TimePilotTokenExpiredReloadCount';
const TOKEN_EXPIRED_RELOAD_MAX = 3;
// Ensures only one 401 handler increments the reload count when many requests fail concurrently
let tokenExpiredReloadInProgress = false;

function getTokenExpiredReloadCount(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  const v = sessionStorage.getItem(TOKEN_EXPIRED_RELOAD_KEY);
  const n = parseInt(v ?? '0', 10);
  return Number.isNaN(n) ? 0 : n;
}

function incrementTokenExpiredReloadCount(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  const count = getTokenExpiredReloadCount() + 1;
  sessionStorage.setItem(TOKEN_EXPIRED_RELOAD_KEY, String(count));
  return count;
}

function clearTokenExpiredReloadCount(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(TOKEN_EXPIRED_RELOAD_KEY);
}

const VERSION_SKEW_RELOAD_ONCE_KEY = 'TimePilotVersionSkewReloadOnce';

// Reload the page if the build ID mismatch
export function ReloadIfVersionMismatch(serverBuildId: string): void {
  const key = 'TimePilotSkewReload';
  const clientBuildId = process.env.BUILD_ID;
  if (process.env.LOCAL_DEV === 'true') return;
  if (!clientBuildId || !serverBuildId) return;
  if (clientBuildId === serverBuildId) {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(VERSION_SKEW_RELOAD_ONCE_KEY);
    }
    return;
  }
  // Stale CDN / browser cache can keep old JS after deploy: one reload does not fix it → infinite loop.
  // Allow at most one version-skew reload per tab session; further mismatch needs hard refresh.
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(VERSION_SKEW_RELOAD_ONCE_KEY) === '1') {
    captureWarning(
      `Version skew persists after reload (likely cached bundle). clientBuildId=${clientBuildId} serverBuildId=${serverBuildId}`,
    );
    return;
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(VERSION_SKEW_RELOAD_ONCE_KEY, '1');
  }
  sessionStorage.setItem(key, 'true');
  captureWarning(`Version Skew Reload: clientBuildId: ${clientBuildId} serverBuildId: ${serverBuildId}`);
  window.location.reload();
}

export function GetBuildId(): string | undefined {
  // TimePilot v0.1.18-2025-08-23T02:22:02.862Z
  if (process.env.BUILD_ID) {
    const versionName = process.env.BUILD_ID?.split('-')[0] || 'TimePilot';
    const buildDate = process.env.BUILD_ID?.split('-')?.slice(1)?.join('-')?.split('T')[0] || '';
    const buildTime = process.env.BUILD_ID?.split('-')?.slice(1)?.join('-')?.split('T')[1]?.split('.')[0] || '';
    return `${versionName} ${buildDate} ${buildTime}`;
  }
  return 'TimePilot';
}

// Use the Build Version Header to check the build version
export function CheckBuildVersionHeader(response: Response): void {
  const serverBuildId = response?.headers?.get('x-app-version') || null;
  if (!!serverBuildId) {
    ReloadIfVersionMismatch(serverBuildId);
  }
}

// an alias for fetch that also checks the build version header
export async function FetchWithVersionCheck(
  url: string,
  options: RequestInit = {},
  initiator: 'user' | 'system' = 'user',
): Promise<Response> {
  // Preserve caller-supplied Authorization when present. Do not read legacy plain
  // accessToken cookies — middleware injects Bearer from the Amplify session for
  // /api/protected/* and /api/proxy/* when credentials: 'include' (via tpFetch).
  const authHeader = (options.headers as Record<string, string> | undefined)?.Authorization
    ?? (options.headers as Record<string, string> | undefined)?.authorization
    ?? '';
  const bearerPrefix = /^Bearer\s+/i;
  const accessToken = authHeader.replace(bearerPrefix, '').trim();

  // Always copy — never mutate the caller's headers object, which may be
  // shared across multiple calls (e.g. a pollHeaders const reused in a loop).
  const newHeaders: Record<string, string> = { ...(options.headers as Record<string, string> ?? {}) };
  if (accessToken) {
    newHeaders['Authorization'] = `Bearer ${accessToken}`;
  }
  try {
    // Delegate to tpFetch: it owns x-tai-user-activity, X-Auth-Idle-Timeout → /api/sign-out
    // redirect, and activityBus.emit. Single source of truth for idle-timeout behaviour.
    const response = await tpFetch(url, {
      ...options,
      headers: newHeaders,
      signal: options.signal ?? AbortSignal.timeout(90000),
      cache: options.cache ?? 'default',
      initiator,
    });
    if (response.ok) {
      CheckBuildVersionHeader(response);
      // Token is valid; clear reload guard so future token expiry can trigger recovery again.
      // activityBus.emit() is handled inside tpFetch — do not call it here (would double-fire).
      clearTokenExpiredReloadCount();
    }
    // CSM Cognito: when token has expired, recover so ALB can refresh or user re-auths.
    // Guard against infinite reload/redirect: if refresh keeps failing (e.g. Cognito/ALB edge case),
    // redirect to logout after TOKEN_EXPIRED_RELOAD_MAX attempts so the user can sign in again.
    // Only one concurrent 401 handler runs: multiple in-flight requests must not each increment the reload count.
    if (response.status === 401 && response.headers.get('X-Auth-Token-Expired') === 'true') {
      if (typeof window !== 'undefined') {
        if (tokenExpiredReloadInProgress) {
          return response;
        }
        tokenExpiredReloadInProgress = true;
        const count = incrementTokenExpiredReloadCount();
        if (count > TOKEN_EXPIRED_RELOAD_MAX) {
          captureWarning(
            `Token expired reload limit reached (${count}); redirecting to logout to break cycle`,
          );
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(TOKEN_EXPIRED_RELOAD_KEY);
          }
          // Automatic, this-browser logout — NOT /logout-confirmation, which calls
          // Cognito GlobalSignOut and revokes the refresh token on every device.
          // That made this recovery path self-sustaining across browsers: a GlobalSignOut
          // anywhere leaves every other tab holding cookies whose refresh token is now
          // revoked, so those tabs 401 with X-Auth-Token-Expired, reload
          // TOKEN_EXPIRED_RELOAD_MAX times, land here, and GlobalSignOut again — killing
          // the session the user had just signed back into and bouncing them to the login
          // page. Same reasoning as the idle-timeout 401 in lib/http/client.ts: an
          // automatic sign-out must not reach past this browser.
          resetAppNavSession(window.location.pathname || '/');
          window.location.href = '/api/sign-out?scope=local';
          return response;
        }
        const isCsmUser = getCookie('tai_csm_user') === '1';
        if (isCsmUser) {
          window.location.href = '/csm';
        } else {
          window.location.reload();
        }
      }
    }
    return response;
  } catch (error) {
    // do nothing for now
    return Promise.resolve(new Response('Error fetching with version check: ' + error, { status: 500 }));
  }
}

// Use the Build Version API to check the build version
export async function CheckBuildVersionViaApi() {
  const response = await FetchWithVersionCheck(`/api/version`, { method: 'HEAD', cache: 'no-store' }, 'system');
}

export const getDownloadUrl = async (
  source: Source,
  session?: { cellId: string; accessToken: string },
) => {
  const resolvedSession = session ?? await getGatewaySession();
  if (!resolvedSession) throw new Error(
    'Your account does not have a cell assignment (custom:cell_id missing from token). ' +
    'Ask an admin to set your cell in Cognito, then sign out and back in.',
  );
  // edge-lambda-service /download expects page as int | null. Source.page is a
  // string ("3", "", undefined); coerce here so empty/non-numeric values become
  // null rather than failing Pydantic validation with a 422.
  const parsedPage = Number.parseInt(source.page ?? '', 10);
  const page = Number.isFinite(parsedPage) ? parsedPage : (source.pageNumber ?? null);
  const sourceFilePath = toDownloadSourceFilePath(source.sourceFilePath ?? '');
  const response = await fetchDownloadUrl({
    cellId: resolvedSession.cellId,
    accessToken: resolvedSession.accessToken,
    sourceFilePath,
    page,
  });
  return {
    ...response,
    signedUrl: resolvePresignedGetUrl(response.signedUrl),
  };
};

export const handleDownload = async (
  sourceObject: MediaSource | Source,
  session?: { cellId: string; accessToken: string },
) => {
  try {
    const rawPath = sourceObject.sourceUrl || sourceObject.sourceFilePath || '';
    const source: Source = {
      index: 0,
      content: '',
      location: '',
      page: '',
      sourceFilePath: rawPath,
      truncatedFilePath: rawPath,
    };

    const url = await getDownloadUrl(source, session);
    window.open(url.signedUrl, '_blank');
  } catch (error) {
    const errorMessage = `Error downloading document: ${error}`;
    captureError(errorMessage, error as Error);
    alert(errorMessage);
  }
};

export function removeMD5Hash(path: string): string {
  // Match pattern: single hyphen followed by 32 hexadecimal characters
  return path.replace(/-[a-f0-9]{32}/g, '');
}

/**
 * Remove a single leading `sharepoint` or `gdrive` segment (case-insensitive) when present.
 * Matches bulk paths like `bulk/DataSyncMT/<site-id>/sharepoint/<folder>/…` — only the
 * prefix immediately after the drive/site id, not a folder named `sharepoint` elsewhere.
 *
 * This fix was requested via:
 * https://tranquility-ai.atlassian.net/browse/TP-2556
 */
const STRIP_LEADING_PATH_SEGMENTS = new Set(['sharepoint', 'gdrive']);

export function stripUnwantedLeadingPathSegments(path: string): string {
  if (!path) return path;
  const segments = path.split('/');
  if (segments.length === 0) return path;
  if (STRIP_LEADING_PATH_SEGMENTS.has(segments[0].toLowerCase())) {
    return segments.slice(1).join('/');
  }
  return path;
}

/**
 * Re-attach the ``data/bulk/`` prefix that provenance ``/files`` strips from
 * ``searchablePath`` for display, producing the canonical key downstream
 * provenance lookups (``/files/file-info``, ``/files/file-status``) and
 * ``/api/proxy/*`` expect for ingested source files.
 *
 * Idempotent: full ``s3://`` URIs and paths that already include ``data/bulk/``
 * pass through unchanged.
 */
export function toProvenanceLookupPath(searchablePath: string | undefined): string {
  const p = (searchablePath ?? '').trim();
  if (!p) return p;
  if (p.startsWith('s3://')) return p;
  if (p.startsWith('data/bulk/') || p.startsWith('/data/bulk/')) return p.replace(/^\/+/, '');
  return `data/bulk/${p.replace(/^\/+/, '')}`;
}

/**
 * Inverse of {@link toProvenanceLookupPath}: strip the ``data/bulk/`` prefix
 * (and any ``s3://<bucket>/`` portion in front of it) so we end up with the raw
 * ``searchable_path`` that turbopuffer indexes — e.g.
 * ``data/bulk/MBOX/Manual/email_10016`` → ``MBOX/Manual/email_10016``.
 *
 * Used at the assistant-service request boundary in ``onQuerySubmit`` so the
 * wire payload sends raw searchable paths even though the in-memory picker
 * state is still ``data/bulk/``-prefixed for downstream provenance lookups.
 * Idempotent on values that are already raw.
 */
export function toSearchablePath(p: string | undefined): string {
  const v = (p ?? '').trim();
  if (!v) return v;
  if (v.startsWith('s3://')) {
    const idx = v.indexOf('/data/bulk/');
    return idx >= 0 ? v.slice(idx + '/data/bulk/'.length) : v;
  }
  return v.replace(/^\/?data\/bulk\//, '');
}

/**
 * Same legacy in-band prefixes as ``app/api/proxy/[...params]/route.ts`` (order: THE_TRANSCRIPT before TRANSCRIPT).
 */
const API_PROXY_LEGACY_INNER_PREFIXES = [
  'THE_TRANSCRIPT',
  'THE_SOURCE',
  'PROCESSHTML',
  'CONVERTEDVIDEO',
  'TRANSCRIPT',
  'DELIVERY',
] as const;

/**
 * Extract the object key from ``s3://bucket/key``. Unlike ``URL`` / ``urlparse``,
 * treats ``#`` as part of the key (not a URL fragment).
 */
export function s3UriToObjectKey(uri: string): string | null {
  const trimmed = uri.trim();
  if (!trimmed.startsWith('s3://')) return null;
  const withoutScheme = trimmed.slice('s3://'.length);
  const slash = withoutScheme.indexOf('/');
  if (slash === -1) return null;
  return withoutScheme.slice(slash + 1);
}

/**
 * True when ``path`` names a processed-bucket extract
 * (``s3://…-processed/files/<hash>/…``), not a turbopuffer-indexed source path.
 * Those URLs play media but DeepDive ``files[]`` filters match zero rows.
 */
export function isProcessedStoragePath(path: string | undefined | null): boolean {
  const p = (path ?? '').trim();
  if (!p) return false;
  if (isArchiveMemberSearchablePath(p)) return false;
  const key = (s3UriToObjectKey(p) ?? p.replace(/^\/+/, '')).replace(/^\/+/, '');
  return /^files\//i.test(key);
}

const CONTAINER_SOURCE_BASENAME_RE = new RegExp(
  `\\.(?:${[...CONTAINER_SELF_EXTENSIONS, 'mbox', 'mbx'].join('|')})$`,
  'i',
);

function indexedSearchablePath(raw: string | undefined | null): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed || isProcessedStoragePath(trimmed)) return '';
  return toSearchablePath(trimmed);
}

function isContainerIndexedPath(indexed: string): boolean {
  return CONTAINER_SOURCE_BASENAME_RE.test(indexed.split(/[?#]/)[0] ?? '');
}

/**
 * Inner path under a processed extract. Leading ``{kind}_processing/`` segments
 * (``archive_processing``, ``mobile_processing``, ``email_processing``,
 * ``docx_processing``, ``media_processing``, …) are processed-bucket layout,
 * not turbopuffer member paths. Other ``files/<hash>/…`` remainders (UFDR
 * relative paths, loose zip members) are kept as-is.
 */
function innerRelativeFromProcessedExtract(path: string): string | undefined {
  const key = (s3UriToObjectKey(path) ?? path.replace(/^\/+/, '')).replace(/^\/+/, '');
  const filesRest = key.match(/^files\/[^/]+\/(.+)$/i)?.[1]?.trim();
  if (!filesRest) return undefined;
  return filesRest.replace(/^[^/]+_processing\//i, '').trim() || undefined;
}

function distinctMemberExtract(
  source: Source | MediaSource,
): string {
  const dispatched = getDispatchedFilePath(source as Source);
  for (const raw of [
    dispatched,
    source.sourceFilePath,
    source.sourceUrl,
    (source as MediaSource).s3Url,
  ]) {
    const trimmed = (raw ?? '').trim();
    if (!trimmed) continue;
    const indexed = indexedSearchablePath(trimmed);
    if (indexed && isContainerIndexedPath(indexed)) continue;
    if (isProcessedStoragePath(trimmed) || (indexed && !isContainerIndexedPath(indexed))) {
      return trimmed;
    }
  }
  return '';
}

/**
 * Path to apply as assistant ``files[]`` / Evidence Locker scope.
 *
 * Zip / UFDR / mbox *members* are indexed under a virtual searchable path
 * (``Case/folder/archive.zip/inner/file.mp3``). SourceCard toolbar actions and
 * Evidence Locker previews often overwrite ``sourceFilePath`` with the processed
 * playback URL, which DeepDive cannot retrieve. Prefer the indexed path.
 *
 * Returns the ``data/bulk/``-prefixed lookup form so in-memory picker state
 * matches tree picks; {@link toSearchablePath} at the chat wire boundary strips
 * the prefix.
 */
export function pathForAssistantScope(
  source: Source | MediaSource | null | undefined,
): string {
  if (!source) return '';

  const citationSearchable = getCitationSearchablePath(source as Source);
  const fileInfoSearchable =
    typeof source.fileInfo?.searchablePath === 'string'
      ? source.fileInfo.searchablePath
      : '';
  for (const raw of [citationSearchable, fileInfoSearchable]) {
    const indexed = indexedSearchablePath(raw);
    if (indexed) return toProvenanceLookupPath(indexed);
  }

  const identityCandidates = [
    source.sourceFilePath,
    source.sourceUrl,
    (source as MediaSource).s3Url,
    source.originalUrl,
  ];
  for (const raw of identityCandidates) {
    const indexed = indexedSearchablePath(raw);
    if (
      indexed &&
      (isArchiveMemberSearchablePath(raw) || isArchiveMemberSearchablePath(indexed))
    ) {
      return toProvenanceLookupPath(indexed);
    }
  }

  const memberExtract = distinctMemberExtract(source);
  const processedForInner = [memberExtract, getDispatchedFilePath(source as Source)].find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && isProcessedStoragePath(candidate),
  );
  const inner =
    (processedForInner ? innerRelativeFromProcessedExtract(processedForInner) : undefined) ||
    (memberExtract && isProcessedStoragePath(memberExtract)
      ? basenameFromStoragePath(memberExtract)
      : undefined);
  if (inner) {
    for (const raw of [source.originalUrl, source.sourceFilePath, source.sourceUrl]) {
      const indexed = indexedSearchablePath(raw);
      if (indexed && isContainerIndexedPath(indexed)) {
        return toProvenanceLookupPath(`${indexed}/${inner}`);
      }
    }
  }

  for (const raw of identityCandidates) {
    const indexed = indexedSearchablePath(raw);
    if (!indexed) continue;
    // After resolveSourceForActions, originalUrl is the parent zip/UFDR/mbox.
    // Never apply that container as scope when a distinct inner extract exists.
    if (memberExtract && isContainerIndexedPath(indexed)) continue;
    return toProvenanceLookupPath(indexed);
  }

  return (source.sourceFilePath || source.sourceUrl || '').trim();
}

/**
 * Strip query / PDF-page fragments for extension lookup without treating ``#``
 * in object keys as a URL fragment (e.g. ``back#drop.jpeg`` stays intact).
 */
export function storagePathForExtensionLookup(path: string): string {
  let p = (path ?? '').trim();
  if (!p) return p;
  const fromS3 = s3UriToObjectKey(p);
  if (fromS3 !== null) return fromS3;
  const q = p.indexOf('?');
  if (q !== -1) p = p.slice(0, q);
  const pageIdx = p.toLowerCase().indexOf('#page=');
  if (pageIdx !== -1) p = p.slice(0, pageIdx);
  return p;
}

/** File extension from a storage path, key, or URL (``#`` in keys is preserved). */
export function extensionFromStoragePath(path: string): string {
  const lookup = storagePathForExtensionLookup(path);
  const base = lookup.split('/').pop() || lookup;
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return '';
  return base.slice(dot + 1).trim().toLowerCase();
}

/**
 * Junkdrawer document endpoint for the URL actually being fetched.
 *
 * Pair this with ``srcToUse``, not the source's original type: a Word/PowerPoint
 * file that already has a ``docx_processing`` / rendered PDF must go to
 * ``/v1/pdf``, while a raw ``.docx`` / ``.pptx`` still needs ``/v1/ms-word`` /
 * ``/v1/ms-powerpoint``. Falls back only when the URL has no document extension.
 */
export function junkdrawerDocumentApiPathFromUrl(
  url: string | undefined | null,
  fallback: 'pdf' | 'ms-word' | 'ms-powerpoint' = 'pdf',
): 'pdf' | 'ms-word' | 'ms-powerpoint' {
  const extension = extensionFromStoragePath((url ?? '').trim());
  if (extension === 'pdf') return 'pdf';
  if (extension === 'doc' || extension === 'docx') return 'ms-word';
  if (extension === 'ppt' || extension === 'pptx') return 'ms-powerpoint';
  return fallback;
}

/**
 * Normalize a storage path for ``POST /download`` so edge-lambda receives a bare
 * object key. Strips ``s3://bucket/`` (without ``urlparse`` fragment truncation)
 * and legacy in-band proxy prefixes.
 */
export function toDownloadSourceFilePath(input: string | undefined | null): string {
  let p = (input ?? '').trim();
  if (!p) return '';

  const fromS3 = s3UriToObjectKey(p);
  if (fromS3 !== null) {
    p = fromS3;
  }

  for (const prefix of API_PROXY_LEGACY_INNER_PREFIXES) {
    if (p.startsWith(prefix)) {
      p = p.slice(prefix.length);
      break;
    }
  }

  return p.replace(/^\/+/, '');
}

/** SigV4 query parameters that mark a URL's query string as an S3 presign. */
const SIGV4_PRESIGN_QUERY_PARAMS = new Set([
  'x-amz-algorithm',
  'x-amz-credential',
  'x-amz-signature',
  'x-amz-security-token',
]);

/**
 * Drop the SigV4 query string from an ``http(s)`` S3 URL, preserving any fragment.
 *
 * edge-lambda resolves bucket/key from the URL text before the ``?`` and then
 * re-presigns with its own credentials, so a signature handed to it is never
 * read. Forwarding one is actively harmful: ``/api/proxy`` carries the whole URL
 * as a single percent-encoded path segment, and the ``X-Amz-Security-Token`` of a
 * role-issued presign is ~1.2KB on its own, so the URI path forwarded to the cell
 * clears AWS WAF's 1024-byte ``SizeRestrictions_URIPATH`` limit and the request
 * comes back 403 ``{"blockedBy": "waf"}``. Non-SigV4 query strings are left alone.
 */
export function stripPresignedS3QueryFromHttpUrl(url: string | undefined | null): string {
  const trimmed = (url ?? '').trim();
  if (!/^https?:\/\//i.test(trimmed)) return trimmed;

  const queryIndex = trimmed.indexOf('?');
  if (queryIndex === -1) return trimmed;

  // Split on the first ``#`` *after* the query only: a literal ``#`` earlier in
  // the string belongs to the object key (``back#drop.jpeg``), not a fragment.
  const afterQuery = trimmed.slice(queryIndex + 1);
  const hashIndex = afterQuery.indexOf('#');
  const query = hashIndex === -1 ? afterQuery : afterQuery.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? '' : afterQuery.slice(hashIndex);

  const isPresign = query
    .split('&')
    .some((pair) => SIGV4_PRESIGN_QUERY_PARAMS.has(pair.split('=')[0].trim().toLowerCase()));
  if (!isPresign) return trimmed;

  return `${trimmed.slice(0, queryIndex)}${fragment}`;
}

/**
 * Normalize a path for ``GET /api/proxy/<encodeURIComponent(this)>`` so it matches the Evidence Locker
 * / unified-sources convention: {@link toProvenanceLookupPath} for ingested keys, ``https`` passthrough
 * (minus any presign query, see {@link stripPresignedS3QueryFromHttpUrl}),
 * ``s3://`` → bare object key (avoids ``#`` fragment truncation in upstream parsers),
 * provenance meta JSON roots ``files/…`` unchanged, legacy ``DELIVERY`` / ``THE_SOURCE`` / … prefixes preserved
 * with the suffix normalized the same way.
 */
export function toEvidenceLockerStyleProxyPath(input: string | undefined | null): string {
  const p = (input ?? '').trim();
  if (!p) return '';
  const fromS3 = s3UriToObjectKey(p);
  if (fromS3 !== null) return fromS3;
  if (/^https?:\/\//i.test(p)) return stripPresignedS3QueryFromHttpUrl(p);
  for (const prefix of API_PROXY_LEGACY_INNER_PREFIXES) {
    if (p.startsWith(prefix)) {
      const rest = p.slice(prefix.length);
      return `${prefix}${toEvidenceLockerStyleProxyPath(rest)}`;
    }
  }
  if (p.startsWith('files/') || p.startsWith('/files/')) {
    return p.replace(/^\/+/, '');
  }
  return toProvenanceLookupPath(p);
}

export function encodeEvidenceLockerStyleProxyUriSegment(path: string | undefined | null): string {
  return encodeS3KeyForProxyPathSegment(toEvidenceLockerStyleProxyPath(path));
}

/**
 * Percent-encode a bare S3 object key for use as the single path segment after
 * ``/api/proxy/``. Uses ``encodeURIComponent`` so spaces become ``%20`` (never
 * ``+``), preserving consecutive spaces and other special characters exactly.
 */
export function encodeS3KeyForProxyPathSegment(key: string): string {
  return encodeURIComponent(key);
}

/**
 * Percent-encode a bare S3 object key for ``?url=`` (and similar) query params on
 * cell-service routes (junkdrawer, provenance, …). Prefer this over
 * ``URLSearchParams``, which encodes spaces as ``+`` and can be mishandled when
 * a downstream layer applies path-style decoding instead of query decoding.
 */
export function encodeS3KeyForUrlQueryParam(key: string): string {
  return encodeURIComponent(key);
}

/**
 * Decode a percent-encoded S3 key parameter exactly once. Safe when the runtime
 * (e.g. Next.js route params) has already decoded the value: leaves strings
 * without ``%HH`` sequences untouched. Unlike ``application/x-www-form-urlencoded``
 * decoding, never treats ``+`` as space.
 */
export function decodePercentEncodedS3KeyParam(value: string): string {
  if (!/%[0-9A-Fa-f]{2}/.test(value)) {
    return value;
  }
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Percent-encode a single URI path segment per AWS SigV4 canonical URI rules
 * (RFC 3986 unreserved set only: A–Z a–z 0–9 - _ . ~).
 */
function awsSigV4EncodeUriComponent(segment: string): string {
  return encodeURIComponent(segment).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`,
  );
}

/**
 * Percent-encode an S3 object key for use in an http(s) URL path (per path segment).
 */
export function encodeS3KeyPathForHttpUrl(key: string): string {
  const normalized = key.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!normalized) return '/';
  return `/${normalized.split('/').map(awsSigV4EncodeUriComponent).join('/')}`;
}

/**
 * Percent-encode literal ``#`` in the path portion of an http(s) URL.
 *
 * ``#`` starts the URL fragment; runtimes drop the fragment and everything after
 * an unencoded ``#`` in the path from the request target. That strips S3 presign
 * query parameters (``?X-Amz-...``) when the object key contains ``#``.
 * Does not alter existing ``%23`` sequences.
 */
export function encodeLiteralHashInHttpUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || !trimmed.includes('#')) return trimmed;
  if (!/^https?:\/\//i.test(trimmed)) return trimmed;
  const q = trimmed.indexOf('?');
  if (q === -1) return trimmed.replace(/#/g, '%23');
  return trimmed.slice(0, q).replace(/#/g, '%23') + trimmed.slice(q);
}

/**
 * Rebuild a presigned PUT URL path from the authoritative S3 ``key`` while
 * preserving the signature query string. Fixes malformed presigned URLs whose
 * path contains a literal ``#`` (which would otherwise truncate the request at
 * ``…/FBI`` and drop ``?X-Amz-…``).
 */
export function rebuildPresignedPutUrlFromKey(uploadUrl: string, key: string): string {
  const normalizedKey = key.trim();
  if (!normalizedKey) return encodeLiteralHashInHttpUrl(uploadUrl);

  const sanitized = encodeLiteralHashInHttpUrl(uploadUrl.trim());
  const queryIndex = sanitized.indexOf('?');
  if (queryIndex === -1) return sanitized;

  const query = sanitized.slice(queryIndex);
  const beforeQuery = sanitized.slice(0, queryIndex);
  const originMatch = beforeQuery.match(/^(https?:\/\/[^/]+)/i);
  if (!originMatch) return sanitized;

  return `${originMatch[1]}${encodeS3KeyPathForHttpUrl(normalizedKey)}${query}`;
}

/**
 * True when ``url`` is an https AWS S3 presigned URL the BFF may server-side fetch.
 * Rejects internal/metadata hosts and non-S3 destinations (SSRF guard).
 */
export function isAllowedS3PresignedHttpUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;

  // Encode literal ``#`` in the path before ``?`` so ``URL`` does not treat the
  // presign query string as a fragment (e.g. ``…/back#drop.jpeg?X-Amz-…``).
  let parsed: URL;
  try {
    parsed = new URL(encodeLiteralHashInHttpUrl(trimmed));
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:') return false;

  const host = parsed.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host.endsWith('.local') ||
    host === '169.254.169.254' ||
    host.startsWith('127.') ||
    host.startsWith('10.') ||
    host.startsWith('192.168.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host) ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(host) ||
    host.startsWith('[')
  ) {
    return false;
  }

  const isAwsS3Host =
    (host.endsWith('.amazonaws.com') || host.endsWith('.amazonaws.com.cn')) &&
    (host.includes('.s3.') ||
      host.includes('.s3-') ||
      host.startsWith('s3.') ||
      host.startsWith('s3-') ||
      host === 's3.amazonaws.com');

  if (!isAwsS3Host) return false;

  const params = parsed.searchParams;
  return params.has('X-Amz-Algorithm') || params.has('X-Amz-Signature');
}

/** Resolve the PUT target for a presigned S3 upload URL. Rebuilds the path from ``key`` only when the key contains ``#`` (browser fragment truncation); otherwise only escapes literal ``#`` already present in the URL string. */
export function resolvePresignedPutUrl(uploadUrl: string, key?: string | null): string {
  const trimmedUrl = uploadUrl.trim();
  const normalizedKey = key?.trim();
  if (!normalizedKey?.includes('#')) {
    return encodeLiteralHashInHttpUrl(trimmedUrl);
  }
  return rebuildPresignedPutUrlFromKey(trimmedUrl, normalizedKey);
}

/**
 * Sanitize a server-signed presigned GET URL for fetch or browser use.
 * Only encodes literal ``#`` in the path — never rebuilds the path from a key,
 * which would change the canonical URI and invalidate ``X-Amz-Signature``.
 */
export function resolvePresignedGetUrl(presignedUrl: string): string {
  return encodeLiteralHashInHttpUrl(presignedUrl.trim());
}

/**
 * Resolve a presigned S3 GET redirect when the BFF follows edge-lambda proxy
 * redirects server-side. Only encodes literal ``#`` in the path; never rebuilds
 * from ``proxyTail`` (that would invalidate the signature).
 */
export function resolvePresignedRedirectFetchUrl(
  presignedLocation: string,
  _proxyTail?: string,
): string {
  return resolvePresignedGetUrl(presignedLocation);
}

/** Basename segment of an S3 object key. */
export function s3ObjectKeyBasename(key: string): string {
  return (key.split('/').pop() ?? '').trim();
}

/** Relative path under ``…/Manual/`` in a bulk delivery object key. */
export function manualUploadRelativePathFromKey(key: string): string | null {
  const marker = '/Manual/';
  const idx = key.lastIndexOf(marker);
  if (idx < 0) return null;
  return key.slice(idx + marker.length);
}

/**
 * Ensure the presigned storage key uses the same basename as the user filename.
 * A key whose basename is ``FBI%23Files.pdf`` is a different object than
 * ``FBI#Files.pdf`` even though downloads may suggest the same display name.
 */
export function assertManualUploadKeyMatchesFileName(key: string, fileName: string): void {
  const trimmedName = fileName.trim();
  if (!trimmedName) {
    throw new Error('fileName is required');
  }

  const relativeFromKey = manualUploadRelativePathFromKey(key);
  if (relativeFromKey === trimmedName) return;

  const basename = s3ObjectKeyBasename(key);
  if (basename === trimmedName) return;

  if (trimmedName.includes('#') && basename === trimmedName.replace(/#/g, '%23')) {
    throw new Error(
      `Storage key "${basename}" encodes "#" as the literal characters "%23". ` +
        `The object must be stored as "${trimmedName}" with a real # in the key.`,
    );
  }

  throw new Error(`Storage key path "${relativeFromKey ?? basename}" does not match filename "${trimmedName}".`);
}

/**
 * Escape literal ``#`` in http(s) URLs so browser fetches and media elements do not
 * treat part of the object key as a URL fragment (the client drops everything
 * after an unencoded ``#``). Does not alter existing ``%23`` sequences.
 */
export function sanitizeHttpUrlForBrowserLoad(url: string): string {
  return encodeLiteralHashInHttpUrl(url);
}

export function sanitizePresignHttpUrls<T extends { uploadUrl?: string; s3Url?: string; key?: string }>(
  presign: T,
): T {
  const uploadUrl =
    typeof presign.uploadUrl === 'string'
      ? resolvePresignedPutUrl(presign.uploadUrl, presign.key)
      : presign.uploadUrl;

  return {
    ...presign,
    ...(typeof uploadUrl === 'string' ? { uploadUrl } : {}),
    ...(typeof presign.s3Url === 'string'
      ? { s3Url: encodeLiteralHashInHttpUrl(presign.s3Url) }
      : {}),
  };
}

/**
 * Normalize a path for the junkdrawer-service ``GET /pre-sign?url=...`` (and
 * sibling) routes. Same shape as {@link toEvidenceLockerStyleProxyPath} —
 * ``http(s)://`` and ``files/...`` pass through, ingested keys flow through
 * {@link toProvenanceLookupPath} — but legacy consumer-side prefixes
 * (``THE_SOURCE``, ``DELIVERY``, …) are stripped outright instead of
 * preserved, because junkdrawer expects a clean S3 URL / bare key.
 */
export function toJunkdrawerObjectUrl(input: string | undefined | null): string {
  const p = (input ?? '').trim();
  if (!p) return '';
  if (/^https?:\/\//i.test(p)) return sanitizeHttpUrlForBrowserLoad(p);
  for (const prefix of API_PROXY_LEGACY_INNER_PREFIXES) {
    if (p.startsWith(prefix)) {
      return toJunkdrawerObjectUrl(p.slice(prefix.length));
    }
  }
  if (p.startsWith('files/') || p.startsWith('/files/')) {
    return p.replace(/^\/+/, '');
  }
  return toProvenanceLookupPath(p);
}

/**
 * Normalize a storage path for junkdrawer-service ``?url=…`` query params.
 * Returns a bare S3 object key (with a literal ``#`` when present in the name).
 */
export function toJunkdrawerServiceUrlParam(input: string | undefined | null): string {
  const trimmed = (input ?? '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('s3://')) {
    const withoutScheme = trimmed.slice('s3://'.length);
    const slash = withoutScheme.indexOf('/');
    return slash === -1 ? '' : withoutScheme.slice(slash + 1);
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(encodeLiteralHashInHttpUrl(trimmed));
      return decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
    } catch {
      return trimmed;
    }
  }

  return toJunkdrawerObjectUrl(trimmed);
}

/**
 * Resolve the storage object the media player actually mounts for ``media``, in the
 * shape junkdrawer's ``/pre-sign`` route and ``/api/proxy`` both accept. The
 * transcoded preview wins over the original upload, which is frequently a codec
 * the browser cannot decode.
 */
export function resolveMediaPlaybackObjectUrl(media: MediaSource | null | undefined): string {
  if (!media) return '';
  let srcToUse = media.mediaContext?.previewUrl || media.mediaSrc || media.mediaUrl || '';
  if (media.fileInfo?.previewUrl) {
    srcToUse = media.fileInfo.previewUrl;
  }
  return toJunkdrawerServiceUrlParam(srcToUse);
}

/**
 * Same-origin ``/api/proxy`` URL for reading a video's bytes into a canvas. The
 * player streams from a presigned S3 URL, so drawing its frames taints the
 * canvas and ``toDataURL`` throws; frame capture re-loads the *same* object from
 * our own origin instead. Deriving it from anything other than
 * {@link resolveMediaPlaybackObjectUrl} risks pointing at an object the browser
 * never played and cannot decode.
 */
export function resolveSameOriginMediaCaptureSrc(
  media: MediaSource | null | undefined,
): string | undefined {
  const objectUrl = resolveMediaPlaybackObjectUrl(media);
  if (!objectUrl) return undefined;
  return `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(objectUrl)}`;
}

/**
 * Prefer the URL the ``<video>`` is actually playing. Playback uses a
 * presigned S3 GET; wrapping that URL in ``/api/proxy`` lets capture re-fetch
 * the same bytes same-origin so the canvas is not tainted. Falls back to
 * {@link resolveSameOriginMediaCaptureSrc} when the element has no src yet.
 */
export function resolveSameOriginCaptureSrcForVideo(
  video: HTMLVideoElement | null | undefined,
  media?: MediaSource | null,
): string | undefined {
  const playing = (video?.currentSrc || video?.src || '').trim();
  if (playing.startsWith('blob:') || playing.startsWith('data:')) {
    return playing;
  }
  if (playing.includes('/api/proxy/')) {
    return playing;
  }
  if (/^https?:\/\//i.test(playing)) {
    return `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(playing)}`;
  }
  return resolveSameOriginMediaCaptureSrc(media);
}

/**
 * Scope rows from `/api/protected/cases/:caseId` are full `case_id` paths. For the Evidence Locker
 * folder list, show only the path under the current case: strip `caseId/` when present; otherwise
 * fall back to dropping the first segment (legacy tenant-only trim).
 */
export function folderPathForEvidenceLockerDisplay(scope: string, caseId: string): string {
  if (!scope) return scope;
  if (caseId && caseId !== '*') {
    const prefix = `${caseId}/`;
    if (scope.startsWith(prefix)) {
      return scope.slice(prefix.length);
    }
    if (scope.includes('/')) {
      return scope.split('/').slice(1).join('/');
    }
  }
  return scope;
}

// Safe URI decoding function that handles malformed URIs
export function safeDecodeURIComponent(str: string): string {
  try {
    if (!str.includes('%2F')) {
      return str;
    }
    return decodeURIComponent(str);
  } catch (e) {
    // If decoding fails, return the original string
    captureWarning(`Failed to decode URI component: ${str}`, e as Error);
    return str;
  }
}

/**
 * File extension from a storage path or URL, treating ``#`` as part of the
 * filename (not a URL fragment delimiter).
 */
export function fileExtensionFromStoragePath(pathOrUrl: string): string {
  return extensionFromStoragePath(pathOrUrl);
}

// use regex instead of looping through SOURCE_TYPES
export function extractPathAfterBulk(objectUrl: string, removeMD5: boolean = false): Source {
  let truncatedPathWithParams;
  let sourceType = 'Unknown';
  // label all extracted sources as type textract
  if (objectUrl && objectUrl.trim() === '') {
    return { truncatedFilePath: undefined, sourceType: 'Unknown' };
  } else if (objectUrl && objectUrl.toLowerCase().includes('/bkb/slow/')) {
    truncatedPathWithParams = safeDecodeURIComponent(objectUrl.slice(objectUrl.toLowerCase().indexOf('/bkb/slow/') + '/bkb/slow/'.length));
    sourceType = 'Textract';
  } else if (objectUrl && objectUrl.trim() !== '') {
    // Create regex pattern for all source types
    const sourceTypesPattern = SOURCE_TYPES.join('|');
    const bulkRegex = new RegExp(`bulk/(${sourceTypesPattern})/`, 'i');
    const match = objectUrl.match(bulkRegex);

    if (match) {
      const rawCaptured = match[1];
      const matchedSourceType =
        SOURCE_TYPES.find((t) => t.toLowerCase() === rawCaptured.toLowerCase()) ?? rawCaptured;
      const bulkMarker = `bulk/${matchedSourceType}/`;
      const index = objectUrl.toLowerCase().indexOf(bulkMarker.toLowerCase());

      // After bulk/<type>/, some sources prefix a drive/site id, then `sharepoint/` and folders.
      // OneDrive and DataSync* use this shape, e.g.:
      // bulk/DataSyncMT/<site-id>/sharepoint/<folder>/file.docx
      //
      // This fix was requested via:
      // https://tranquility-ai.atlassian.net/browse/TP-2556
      const stripSharePointAfterDriveId = (t: string) => t === 'OneDrive' || t === 'DataSyncMT';

      switch (matchedSourceType) {
        case 'GoogleDrive':
        case 'OneDrive':
        case 'DataSyncMT':
          // Remove the drive / site id from the path as well
          const driveIdAndPath = objectUrl.slice(index + bulkMarker.length);
          const driveIdIndex = driveIdAndPath.indexOf('/');
          if (driveIdIndex !== -1) {
            let rest = driveIdAndPath.slice(driveIdIndex + 1);
            if (stripSharePointAfterDriveId(matchedSourceType)) {
              rest = stripUnwantedLeadingPathSegments(rest);
            }
            truncatedPathWithParams = rest;
            sourceType = matchedSourceType;
            break;
          }
          truncatedPathWithParams = driveIdAndPath;
          sourceType = matchedSourceType;
          break;
        default:
          truncatedPathWithParams = objectUrl.slice(index + bulkMarker.length);
          sourceType = matchedSourceType;
          break;
      }
    } else {
      // If we haven't found a path yet but the URL contains 'bulk/', extract everything after it
      const bulkMatch = objectUrl.toLowerCase().match(/bulk\//);
      if (bulkMatch) {
        truncatedPathWithParams = objectUrl.slice(objectUrl.toLowerCase().indexOf('bulk/') + 'bulk/'.length);
      }
    }
    if (truncatedPathWithParams) {
      truncatedPathWithParams = safeDecodeURIComponent(truncatedPathWithParams);
    } else {
      // For s3:// URIs that don't contain a bulk/ path (e.g. s3://bucket/files/<uuid>/path/file.pdf),
      // strip the bucket prefix first, then strip the files/<uuid>/ tenant prefix if present,
      // so only the meaningful relative path is shown/copied.
      const objectKey = s3UriToObjectKey(objectUrl) ?? objectUrl;
      const filesMatch = objectKey.match(/^files\/[^/]+\/(.*)/i);
      truncatedPathWithParams = safeDecodeURIComponent(filesMatch ? filesMatch[1] : objectKey);
    }
  }

  const truncatedFilePath = truncatedPathWithParams ? (removeMD5 ? removeMD5Hash(truncatedPathWithParams.split('?')[0]) : truncatedPathWithParams.split('?')[0]) : truncatedPathWithParams;

  return { truncatedFilePath, sourceType };
}

export const isRestrictedFileType = (source: Source | MediaSource | MediaSource): boolean => {
  const sourceIn: any = source;
  const url = sourceIn.sourceUrl || sourceIn.mediaUrl || sourceIn.mediaSrc || sourceIn.sourceFilePath;
  const extension = fileExtensionFromStoragePath(url ?? '');
  const extension2 = (url.split('.')?.at(-1) || '').trim().toLowerCase();
  return RestrictedSourceTypes.includes(extension) || RestrictedSourceTypes.includes(extension2);
}

// do not use as an export, but use as a part of the getMediaType function below.
//
// NOTE: ``previewFilePath`` is intentionally *not* consulted here. A container
// citation's preview is only a renderable proxy — a jpg/png/webp thumbnail or a
// waveform image — for the real extract, so trusting its extension would let it
// short-circuit the authoritative provenance ``fileInfo`` stream/mime branch in
// ``getMediaType`` (an extensionless video/audio extract cited with a
// ``…_thumb.jpg`` preview would be misclassified as an image). The preview
// extension is applied as a lower-priority fallback in ``getMediaType`` via
// ``guessMediaTypeFromPreview`` instead.
const guessMediaType = (
  source: Source | MediaSource | MediaSource
): MediaType => {
  let declaredMediaType = 'unknown';
  let sourceIn: any = source;
  if (sourceIn.sourceType && sourceIn.sourceType !== '' && !Array.isArray(sourceIn.sourceType)) {
    declaredMediaType = sourceIn.sourceType as MediaType;
  } else if (sourceIn.mediaType && sourceIn.mediaType !== '') {
    declaredMediaType = sourceIn.mediaType as MediaType;
  }
  if (!sourceIn.sourceUrl && !sourceIn.mediaUrl && !sourceIn.mediaSrc && !sourceIn.sourceFilePath && !sourceIn.source_s3_path) return declaredMediaType as MediaType;
  const url = sourceIn.sourceUrl || sourceIn.mediaUrl || sourceIn.mediaSrc || sourceIn.sourceFilePath || sourceIn.source_s3_path;
  const extension = fileExtensionFromStoragePath(url ?? '');
  // the following is for s3 urls that have # in the actual filename
  const extension2 = (url.split('.')?.at(-1) || '').trim().toLowerCase();
  if (FileTypes[extension as keyof typeof FileTypes]) {
    return FileTypes[extension as keyof typeof FileTypes] as MediaType;
  } else if (FileTypes[extension2 as keyof typeof FileTypes]) {
    return FileTypes[extension2 as keyof typeof FileTypes] as MediaType;
  } else if (declaredMediaType !== 'unknown') {
    return declaredMediaType as MediaType;
  } else {
    return 'unknown' as MediaType;
  }
}

/**
 * Media type implied by a citation's ``previewFilePath`` extension. The preview
 * is only a *proxy* for the real extract (a thumbnail / waveform image), so this
 * is a low-priority signal: {@link getMediaType} applies it only after the
 * provenance ``fileInfo`` stream/mime branch has had its say, so a container
 * citation's ``_thumb.jpg`` can't mask an extensionless video/audio extract.
 */
const guessMediaTypeFromPreview = (source: Source | MediaSource): MediaType => {
  const preview = (source as any).previewFilePath;
  if (typeof preview === 'string' && preview !== '') {
    const previewExtension = extensionFromStoragePath(preview);
    const mapped = FileTypes[previewExtension as keyof typeof FileTypes] as MediaType | undefined;
    if (mapped) return mapped;
  }
  return 'unknown' as MediaType;
}

export const checkSourceContentType = (fileInfo: FileSummary | null | undefined): MediaType | undefined => {
  if (fileInfo?.mimeType) {
    // Strip parameters (e.g. "; charset=utf-8") so "text/html; charset=utf-8" matches type-specific cases
    const contentType = fileInfo.mimeType.split(';')[0].trim().toLowerCase();
    const mainContentType = (contentType.split('/')[0] || 'unknown').toLowerCase();
    switch (contentType) {
      case 'text/html':
        return 'html';
      case 'text/plain':
        return 'text';
      case 'text/xml':
        return 'text';
      case 'text/markdown':
        return 'markdown';
      case 'application/xml':
        return 'text';
      case 'application/pdf':
        return 'pdf';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'docx';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'spreadsheet';
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        return 'pptx';
      case 'application/vnd.ms-powerpoint':
        return 'pptx';
      case 'application/json':
      case 'application/ld+json':
      case 'text/json':
        return 'json';
      default:
        switch (mainContentType) {
          case 'image':
            return 'image';
          case 'video':
            return 'video';
          case 'audio':
            return 'audio';
          case 'text':
            return 'text';
          default:
            return undefined;
        }
    }
  }
  return undefined;
}

export const getMediaType = (source: Source | MediaSource, fileInfo?: FileSummary | null | undefined): MediaType => {
  // ``guessMediaType`` can echo back ``source.sourceType`` / ``source.mediaType``
  // verbatim, which for chat citations is often the string ``"Unknown"``
  // (capital U — the server-side sentinel for "no idea"). A strict
  // ``=== 'unknown'`` comparison treats that as a real, found type and
  // short-circuits both the ``fileInfo`` branch *and* the path-segment
  // fallback below, so we normalise to lowercase and treat any case-insensitive
  // ``"unknown"`` as not-yet-found.
  const rawExtensionType = guessMediaType(source);
  const mediaTypeFromExtension = (
    typeof rawExtensionType === 'string' ? (rawExtensionType.toLowerCase() as MediaType) : rawExtensionType
  );
  let thisMediaType: MediaType | undefined = mediaTypeFromExtension;
  let foundMediaType = mediaTypeFromExtension !== 'unknown';
  const fileInfoToUse = !!fileInfo ? fileInfo : source.fileInfo;
  const typedPath =
    source.sourceFilePath ||
    source.sourceUrl ||
    (source as MediaSource).mediaUrl ||
    (source as MediaSource).mediaSrc ||
    '';
  // When an explicit ``fileInfo`` is passed it describes a *subdocument* (e.g.
  // an extensionless media extract pulled out of a UFDR/zip). The ``source``
  // itself frequently still carries the *parent container* in
  // ``sourceFilePath`` — its ``.ufdr`` / ``.zip`` extension would make
  // ``isContainerLikeSource`` classify this row as a container and skip the
  // fileInfo branch below (stream flags, mime, categories) that is the only
  // signal for these extensionless extracts. Judge container-likeness from the
  // subdocument's *own* signals (mime / extension / categories, plus its own
  // ``fileUrl``) and never the parent-container path in that case.
  // ``sourceLikeForPathOnly`` scrubs the sibling citation url fields
  // (``previewFilePath`` / ``dispatchedFilePath`` / ``mediaUrl`` / …) so the
  // parent path cannot sneak back in through ``isContainerLikeSource``'s
  // path-fallback. Genuine nested containers still self-report via their own
  // mime/extension/lineage on the fileInfo. When no explicit fileInfo is
  // supplied the source *is* the extract, so its own path is the right signal.
  const containerScopedSource = fileInfo
    ? sourceLikeForPathOnly(source, fileInfoToUse?.fileUrl || '')
    : ({
        ...source,
        sourceFilePath: typedPath || fileInfoToUse?.fileUrl || '',
        sourceUrl: typedPath || fileInfoToUse?.fileUrl || '',
      } as Source);
  const containerFileInfo =
    !!fileInfoToUse && isContainerLikeSource(containerScopedSource, fileInfoToUse);
  if (!foundMediaType && (!!fileInfo || !!source.fileInfo) && !containerFileInfo) {
    if (fileInfoToUse?.isImage) {
      thisMediaType = 'image';
      foundMediaType = true;
    } else if (isAudioVideoFileInfo(fileInfoToUse) && !foundMediaType) {
      // Prefer the authoritative stream flags, then the mime prefix, then a
      // case-insensitive content-category check. ``hasVideoStream`` wins so a
      // video-with-audio track (both streams present) still mounts ``<video>``,
      // and files whose only signal is ``video/mp4`` mime or a lowercase
      // ``video`` category are no longer misclassified as audio (TP: source
      // viewer rendering extensionless UFDR video extracts as audio).
      const avMime = (fileInfoToUse?.mimeType || '').toLowerCase();
      const avCategories = (fileInfoToUse?.contentCategories || []).map((c) => String(c).toLowerCase());
      const isVideo =
        fileInfoToUse?.hasVideoStream === true ||
        avMime.startsWith('video/') ||
        avCategories.includes('video');
      thisMediaType = isVideo ? 'video' : 'audio';
      foundMediaType = true;
    }
    if (fileInfoToUse?.mimeType && !foundMediaType) {
      const mimeMediaType = checkSourceContentType(fileInfoToUse);
      if (!!mimeMediaType && mimeMediaType !== 'unknown' && mediaTypeFromExtension === 'unknown') {
        thisMediaType = mimeMediaType;
        foundMediaType = true;
      }
    }
    if (fileInfoToUse?.extension && !foundMediaType) {
      const ft = fileInfoToUse.extension.toLowerCase();
      const mappedType = FileTypes[ft as keyof typeof FileTypes] as MediaType | undefined;
      if (mappedType && mappedType !== 'unknown') {
        thisMediaType = mappedType;
        foundMediaType = true;
      }
    }
    if (fileInfoToUse?.contentCategories && fileInfoToUse?.contentCategories.length > 0 && !foundMediaType) {
      for (const category of fileInfoToUse?.contentCategories || []) {
        const mediaTypeFromFileInfo: MediaType = category.toLowerCase() as MediaType;
        if (mediaTypeFromFileInfo && USABLE_MEDIA_TYPES.includes(mediaTypeFromFileInfo)) {
          thisMediaType = mediaTypeFromFileInfo;
          foundMediaType = true;
          break;
        }
      }
    }
  }
  if (!foundMediaType) {
    // Preview is only a renderable proxy for the real extract, so its extension
    // is applied *after* the provenance stream/mime branch above — a container
    // citation's ``_thumb.jpg`` / ``_waveform.png`` must not shadow an
    // extensionless video/audio extract. When no authoritative file-info was
    // available, the preview extension is still a useful last-resort signal.
    const rawPreviewType = guessMediaTypeFromPreview(source);
    const previewType =
      typeof rawPreviewType === 'string'
        ? (rawPreviewType.toLowerCase() as MediaType)
        : rawPreviewType;
    if (previewType !== 'unknown') {
      thisMediaType = previewType;
      foundMediaType = true;
    }
  }
  return thisMediaType || mediaTypeFromExtension || ('unknown' as MediaType);
}

/**
 * Effective display type for an already-built media row (chat / timeline media).
 *
 * Content-only JSON rows (`mediaType: 'json'`, no `mediaUrl`/`mediaSrc`) keep
 * a parent archive path on `sourceFilePath` / `sourceUrl` (mbox, zip, …).
 * Path-based {@link getMediaType} would reclassify those as non-displayable and
 * drop rows that upstream already accepted as JSON media.
 */
export function getMediaRowDisplayType(
  mediaItem: MediaSource,
  fileInfo?: FileSummary | null,
): MediaType {
  const hasMediaUrl = !!(mediaItem.mediaUrl?.trim() || mediaItem.mediaSrc?.trim());
  if (!hasMediaUrl && mediaItem.mediaType === 'json') {
    return 'json';
  }
  return getMediaType(mediaItem, fileInfo ?? mediaItem.fileInfo);
}

/**
 * Internal case name (no tenant prefix). API calls and storage paths use this
 * value, not {@link getCaseDisplayName}.
 */
export const getCaseInternalName = (caseId: string | undefined | null): string => {
  if (!caseId) return '';
  if (caseId === '*') return '*';
  const parts = caseId.split('/');
  return parts.length > 1 ? parts.slice(1).join('/') : caseId;
};

/**
 * Converts a caseId to a display name.
 * Handles '*' → 'All Cases', maps Ad Hoc → 'My Files', and removes tenant prefix if present.
 * @param caseId - The case ID (may include tenant prefix like 'tenant/caseName' or be '*')
 * @returns The display name for the case
 */
export const getCaseDisplayName = (caseId: string | undefined | null): string => {
  if (!caseId) return '';
  if (caseId === '*') return 'All Cases';
  const internal = getCaseInternalName(caseId);
  if (isPersonalFilesCaseId(internal)) {
    return AD_HOC_CASE_DISPLAY_NAME;
  }
  return internal;
};

/**
 * Formats timeline text by replacing '*' with 'All Cases'.
 * Used for timeline titles and descriptions that may contain '*'.
 * @param text - The text to format (may be null/undefined)
 * @returns The formatted text with '*' replaced by 'All Cases'
 */
export const formatTimelineDisplayText = (
  text: string | null | undefined,
  allCasesLabel = 'All Cases',
): string => {
  if (!text) return text || '';
  return text.replace(/\*/g, allCasesLabel);
};
